import { getAuthHeaders } from '@/hooks/useAuth';
import { ReplayQuestions } from '@/components/ReplayQuestions';
import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { renderRichText } from '@/lib/richText';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Loader2, ChevronLeft, Share2, Video, Play, Eye, Lock, Users, FileText, ImageIcon, File, X, CalendarDays, Clock, Ticket, Paperclip, Pencil, Check, MapPin, RotateCcw, Building2, MessageSquare } from 'lucide-react';
import {
  fetchPublicProduct,
  agencyInviteCode,
  fetchProductReviews,
  fetchMemberStatus,
  fetchJoinLink,
  checkoutProduct,
  guestCheckout,
  joinWaitlist,
  requestEarlyAccess,
  validateDiscountCode,
  formatClock,
  submitRegistrationAnswers,
  type RegistrationAnswers,
  type CreatorProduct,
  type ProductVideo,
  type CourseLesson,
  type PublicProductCreator,
  type ReviewSummary,
  type MemberStatus,
  type BundleSummary,
  cancelMyRegistration,
} from '@/services/creatorApi';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';
import { trackViewContent, trackInitiateCheckout, trackPurchase, trackFreeRegistration } from '@/lib/marketing';
import { NextEventCrossSell } from '@/components/NextEventCrossSell';
import GoodsProductView from '@/components/GoodsProductView';
import { fetchWallet } from '@/services/affiliateApi';
import { formatEventParts } from '@/lib/eventTime';
import { RegistrationQuestionsForm, answersComplete } from '@/components/RegistrationQuestionsForm';
import { InquiryForm } from '@/components/InquiryForm';
import { useOctOffer, offerPageHeadline, offerScarcityLine } from '@/services/offerApi';

// Lazy-loaded — only fetched when a visitor opens the preview.
const MuxPlayer = lazy(() => import('@mux/mux-player-react'));

const STATIC_ROOT = '/api/s3/';

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// Runtime label: minutes under 2 hours, else hours (e.g. 270 → "4h 30m", 240 → "4 hours").
function formatDuration(mins: number): string {
  if (mins < 120) return `${mins} minutes`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hours` : `${h}h ${m}m`;
}

// Dated class-feed products (e.g. ITA) render an event treatment instead of a curriculum.
// Viewer's own time zone, zone abbreviation included (see lib/eventTime).
const formatEventDate = (iso?: string) => formatEventParts(iso, 'long');

type CurriculumItem = ProductVideo | CourseLesson;

// One person presenting — the unit a rotating show is actually made of.
type Presenter = { name: string; role: string | null; image: string | null };

const initialsOf = (name: string) => name
  .replace(/[^A-Za-z\s'-]/g, ' ')          // drop credits like ", CSA" before initialing
  .split(/\s+/).filter(Boolean).slice(0, 2)
  .map(w => w[0].toUpperCase()).join('') || '?';

// Faces, names and billing for whoever is running a given date. Lives high on the
// page — directly under the title and date — because on a show that rotates hosts
// weekly, WHO is on this week is most of the reason to register for this week.
// Inherits its ink from the surrounding panel (`.hj-event-paper` remaps the
// tokens on cream), so the same markup works on beige paper and on dark.
const RAIL_FACE = 46;

const PresenterRail: React.FC<{ label: string; people: Presenter[] }> = ({ label, people }) => {
  if (!people.length) return null;
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--hj-hairline)' }}>
      <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 8 }}>{label}</div>
      <div className="flex flex-wrap" style={{ gap: '10px 18px' }}>
        {people.map((h, i) => {
          const src = h.image
            ? (h.image.startsWith('http') || h.image.startsWith('/api/') ? h.image : `${STATIC_ROOT}${h.image}`)
            : null;
          return (
            <div key={`${h.name}-${i}`} className="flex items-center gap-2.5" style={{ minWidth: 0 }}>
              {/* Initials tile underneath, photo on top. A presenter with no
                  headshot yet — and a dead image key, which hides the photo — both
                  land on the tile instead of a gap or a broken-file glyph. */}
              <span
                className="hj-headshot flex items-center justify-center flex-shrink-0 relative overflow-hidden"
                style={{ width: RAIL_FACE, height: RAIL_FACE, background: 'var(--hj-surface-2)', color: 'var(--hj-ink-2)', fontSize: 16, fontWeight: 700, letterSpacing: '0.02em' }}
              >
                <span aria-hidden>{initialsOf(h.name)}</span>
                {src && (
                  <img
                    src={src}
                    alt={h.name}
                    className="hj-headshot absolute inset-0"
                    style={{ width: '100%', height: '100%' }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)', lineHeight: 1.25 }}>{h.name}</div>
                {h.role && <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.3 }}>{h.role}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function fileIconFor(fileType?: string) {
  switch (fileType) {
    case 'pdf': return <FileText className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />;
    case 'image': return <ImageIcon className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />;
    case 'file': return <File className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />;
    default: return <Video className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />;
  }
}

const CoursePage: React.FC = () => {
  // A named landing (/john-wroblewski) has no :productId — the path segment IS
  // the identifier, and the API resolves it against the product's vanityCode.
  const { productId: routeProductId } = useParams<{ productId: string }>();
  const productId = routeProductId || window.location.pathname.replace(/^\/+|\/+$/g, '');
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, user } = useAuthContext();
  // October offer: pitched next to a paid ticket to people without paid Plus.
  const octOffer = useOctOffer();

  const [product, setProduct] = useState<CreatorProduct | null>(null);
  // Only the public product fetch knows how to resolve a vanity. Every other
  // endpoint takes an ObjectId, so a named landing has to switch to the real _id
  // the moment the product lands or checkout, member pricing and the discount
  // check all quietly 404 against the path segment.
  const apiId = product?._id ? String(product._id) : productId;
  const [creator, setCreator] = useState<PublicProductCreator | null>(null);
  const [reviews, setReviews] = useState<ReviewSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  // Guest checkout (signed out, paid event): email first, then Stripe at the public
  // price, or a "you're a member, sign in" stop if the email holds a Plus membership.
  const [guestMode, setGuestMode] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  // Agency invite link, signed out: name + email is enough (we make the account).
  // The register box renders twice on event pages (under the class details and in
  // the sidebar). Anything that opens inside it — the host's questions, the guest
  // form, the early-access form — opens only in the copy that was clicked.
  const [ctaSlot, setCtaSlot] = useState<'details' | 'side'>('side');
  const [guestFirst, setGuestFirst] = useState('');
  const [guestLast, setGuestLast] = useState('');
  // One-click registration. A link we emailed carries ?register=<token>; the
  // payload is readable (the signature isn't), so the page knows which address
  // the link was sent to before the server ever sees it. Signed in as that
  // address: register on load. Signed out: the email is filled and locked and
  // one "Confirm my seat" tap holds the seat by email. A confirm tap, not the
  // page load, does the writing, so link scanners can't register anyone.
  const registerToken = new URLSearchParams(location.search).get('register') || '';
  const registerFor = (() => {
    try {
      if (!registerToken) return null;
      const d = JSON.parse(atob(registerToken.split('.')[0].replace(/-/g, '+').replace(/_/g, '/')));
      return d && d.e && d.p ? { email: String(d.e).toLowerCase(), productId: String(d.p), occurrence: d.d || null } : null;
    } catch { return null; }
  })();
  const [registerDone, setRegisterDone] = useState(false);
  // An event pulled from sale keeps its page as a notice (see the public endpoint).
  const [postponed, setPostponed] = useState<{ title: string; notice: string } | null>(null);
  const [heldByLink, setHeldByLink] = useState(false);   // seat held by email via the link, no account
  const [guestNotice, setGuestNotice] = useState<string | null>(null);
  const guestSuccess = heldByLink || (new URLSearchParams(location.search).get('purchased') === 'success' && new URLSearchParams(location.search).get('guest') === '1');
  // Guests come back here rather than /purchases, so the Purchase event has to
  // fire on this page too — guest checkout is how most buyers actually pay.
  const purchasedSessionId = new URLSearchParams(location.search).get('session_id');

  // One product, more than one audience. ?v=<key> swaps the headline and body
  // copy for an entry in product.altLandings, leaving price, tiers, checkout and
  // the registration list alone — so a hockey audience and an actor audience can
  // be sold the same event without a second product splitting the attendee list
  // (which is what went wrong with the duplicate Waiting Room on Aug 7).
  const altKey = new URLSearchParams(location.search).get('v');
  const alt = React.useMemo(() => {
    if (!altKey || !product) return null;
    const list = ((product as any).altLandings || []) as Array<{ key: string; title?: string; description?: string }>;
    return list.find(a => a.key === altKey) || null;
  }, [altKey, product]);
  const displayTitle = alt?.title || product?.title || '';
  const displayDescription = alt?.description || product?.description || '';
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [waitlistState, setWaitlistState] = useState<'idle' | 'joining' | 'joined'>('idle');
  const [earlyAccessState, setEarlyAccessState] = useState<'idle' | 'joining' | 'joined'>('idle');
  // A published event whose dates genuinely aren't set yet: there is nothing to
  // register for, so the page offers to tell you when there is.
  const [watchState, setWatchState] = useState<'idle' | 'joining' | 'joined'>('idle');
  const [showPreview, setShowPreview] = useState(false);
  // The preview starts on its own, muted (browsers only autoplay muted video).
  // Nobody clicked the old "Watch free preview" button. Opening it by hand
  // after closing it plays with sound.
  const [previewMuted, setPreviewMuted] = useState(true);
  // Signed-lesson preview tokens (null = not needed/failed; undefined = loading).
  // IMPORTANT: everything is derived INSIDE the effect from state — referencing
  // the render-scope preview consts here is a temporal-dead-zone crash (they're
  // declared further down; shipped a white screen on the MYST page once).
  const [previewTokens, setPreviewTokens] = useState<{ playback: string; thumbnail?: string } | null | undefined>(null);
  useEffect(() => {
    if (!purchasedSessionId || !product?._id) return;
    trackPurchase({ eventId: purchasedSessionId, productId: String(product._id), title: product.title, valueCents: product.price });
  }, [purchasedSessionId, product?._id, product?.title, product?.price]);

  useEffect(() => {
    if (!showPreview || !product) return;
    const clipReady = product.muxPreview?.muxIngestStatus === 'ready' && !!product.muxPreview?.muxPlaybackId;
    if (clipReady) return; // dedicated preview clip is public — no token needed
    const idx = (product.videos || []).findIndex(
      (v: any) => v.freePreview && v.muxIngestStatus === 'ready' && v.muxPlaybackId
    );
    if (idx < 0) return;
    let cancelled = false;
    setPreviewTokens(undefined);
    fetch(`/api/mux/playback-token/${apiId}/${idx}`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled) return;
        setPreviewTokens(d && d.tokens && d.tokens.playback
          ? { playback: d.tokens.playback, thumbnail: d.tokens.thumbnail || undefined }
          : null);
      })
      .catch(() => { if (!cancelled) setPreviewTokens(null); });
    return () => { cancelled = true; };
  }, [showPreview, product, productId]);
  useEffect(() => {
    if (!product?._id) return;
    const playable = (v: any) => !!v && !!v.freePreview && (!!v.url || (v.muxIngestStatus === 'ready' && !!v.muxPlaybackId));
    const clipReady = product.muxPreview?.muxIngestStatus === 'ready' && !!product.muxPreview?.muxPlaybackId;
    const lessons = [...(product.videos || []), ...((product.modules || []) as any[]).flatMap((m: any) => m.lessons || [])];
    if (clipReady || lessons.some(playable)) setShowPreview(true);
  }, [product?._id]);
  const [memberStatus, setMemberStatus] = useState<MemberStatus | null>(null);
  // Creator (or admin) viewing their own listing — offer the full course view
  // instead of purchase locks.
  const [viewerIsOwner, setViewerIsOwner] = useState(false);
  // Unpublished, reached by direct link: visible to anyone who has it, not buyable.
  const [previewOnly, setPreviewOnly] = useState(false);
  // Agency-only events: who it's for, and whether this viewer qualifies (null = signed out).
  const [access, setAccess] = useState<{ restrictedTo: string; eligible: boolean | null; earlyAccess?: boolean; viaInvite?: boolean } | null>(null);
  // Free things registering also enrolls you in (product.autoEnrollProductIds), by title.
  const [autoEnroll, setAutoEnroll] = useState<string[]>([]);
  const [autoEnrollTaglines, setAutoEnrollTaglines] = useState<string[]>([]);
  // productType 'bundle': what's inside, what it costs bought separately, and whether it can be bought.
  const [bundle, setBundle] = useState<BundleSummary | null>(null);
  // Selected ticket tier key (live events with participant/observer seats).
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  // Recurring featured event: which session the buyer is registering for (ISO string).
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  // Discount code ("Have a code?") — validated server-side before checkout.
  const [showCodeField, setShowCodeField] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<{ code: string; amountOffCents: number; expiresAt?: string | null } | null>(null);
  // Featured event join link — only resolves for registered buyers (or the creator).
  const [joinLink, setJoinLink] = useState<{ joinUrl: string; joinProvider: string; eventDate?: string; durationMins?: number | null; questions?: CreatorProduct['registrationQuestions']; answers?: RegistrationAnswers | null; downloads?: CreatorProduct['downloads'] } | null>(null);
  // Creator's registration questions: answered after registering (card under the
  // join link) or alongside an early-access request on an agency-only event.
  const [qAnswers, setQAnswers] = useState<RegistrationAnswers>({});
  const [qState, setQState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);
  // Free event with questions: the first click opens the form, the form's button
  // registers. Same two-step as early access, for the same reason — the answers
  // have to come in WITH the registration or they never come at all.
  const [askOpen, setAskOpen] = useState(false);
  const askedFromEmail = new URLSearchParams(location.search).get('ask') === '1';

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    fetchPublicProduct(productId)
      .then(({ product, creator, viewerIsOwner, previewOnly, access, watchingDates, postponed, notice, bundle, autoEnroll, autoEnrollTaglines }: any) => {
        if (postponed) { setPostponed({ title: product?.title || '', notice: String(notice || '') }); return; }
        setProduct(product);
        setCreator(creator);
        setViewerIsOwner(!!viewerIsOwner);
        setPreviewOnly(!!previewOnly);
        setAccess(access || null);
        setAutoEnroll(Array.isArray(autoEnroll) ? autoEnroll : []);
        setAutoEnrollTaglines(Array.isArray(autoEnrollTaglines) ? autoEnrollTaglines : []);
        setBundle(bundle || null);
        if (access?.earlyAccess) setEarlyAccessState('joined');
        if (watchingDates) setWatchState('joined');
        // Ad funnel: this is the landing page for paid traffic. Skip the creator's
        // own views so previewing your product doesn't pollute the audience Meta
        // builds its lookalikes from.
        if (!viewerIsOwner) trackViewContent(product);
        // Default the tier picker to the lowest-priced tier that still has seats — the
        // entry option (e.g. the $25 observer seat, not the $75 hot seat).
        const ts = product.ticketTiers;
        if (product.pricingMode !== 'subscription' && ts && ts.length) {
          const avail = ts.filter(t => !t.soldOut && !(t as any).nonMembersOnly && !(t as any).membersOnly);
          // (a non-members-only tier is never the default: it's for a cold visitor
          // to choose, and a member can't buy it at all)
          const pick = (avail.length ? avail : ts).slice().sort((a, b) => a.priceCents - b.priceCents)[0];
          setSelectedTier(pick.key);
        }
        fetchProductReviews(String(product._id))
          .then(setReviews)
          .catch(() => setReviews({ reviews: [], averageRating: 0, totalReviews: 0 }));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [productId]);

  // Per-buyer member pricing (member price + annual-cap status). Auth only.
  useEffect(() => {
    if (!apiId || !isAuthenticated) { setMemberStatus(null); return; }
    fetchMemberStatus(apiId).then(setMemberStatus).catch(() => setMemberStatus(null));
  }, [apiId, isAuthenticated]);

  // Hey Joe credit (referral rewards, in dollars). Applied to the ticket by
  // default when there is any — a reward nobody spends is a reward nobody feels.
  const [walletCents, setWalletCents] = useState(0);
  const [useWallet, setUseWallet] = useState(true);
  useEffect(() => {
    if (!isAuthenticated) { setWalletCents(0); return; }
    fetchWallet().then(w => setWalletCents(w.balanceCents || 0)).catch(() => setWalletCents(0));
  }, [isAuthenticated]);

  // Reveal the featured event join link once the buyer is registered (404/403 → stays null).
  useEffect(() => {
    if (!apiId || !isAuthenticated || product?.productType !== 'webinar') { setJoinLink(null); return; }
    fetchJoinLink(apiId).then(setJoinLink).catch(() => setJoinLink(null));
  }, [apiId, isAuthenticated, product?.productType]);

  // `silent` suppresses the error toast for codes that came from the URL rather
  // than the visitor — an expired promo link shouldn't greet an ad click with a
  // red error. It just quietly falls back to full price.
  const applyCode = async (raw: string, { silent = false } = {}) => {
    const code = raw.trim();
    if (!apiId || !code) return;
    setApplyingCode(true);
    try {
      const r = await validateDiscountCode(apiId, code);
      if (r.valid && r.code) {
        setAppliedDiscount({ code: r.code, amountOffCents: r.amountOffCents || 0, expiresAt: r.expiresAt ?? null });
        setShowCodeField(false);
      } else if (!silent) {
        toast({ title: "That code isn't valid for this class.", variant: 'destructive' });
      }
    } finally {
      setApplyingCode(false);
    }
  };

  const handleApplyCode = () => applyCode(codeInput);

  // Auto-apply ?code= so a paid ad can link straight to the discounted price with
  // no "enter this code" step — that field is real friction on cold traffic.
  // Runs once per product; a code the visitor typed themselves always wins.
  useEffect(() => {
    if (!apiId || appliedDiscount) return;
    // `club` is the same thing under the name the outreach used: each hockey
    // club's link carries its own code, so the family never types one in.
    const qs = new URLSearchParams(location.search);
    const urlCode = qs.get('code') || qs.get('club');
    if (urlCode) applyCode(urlCode, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiId]);

  const [cancelling, setCancelling] = useState(false);
  const handleCancelRegistration = async () => {
    const reg = memberStatus?.myRegistration;
    if (!reg || cancelling) return;
    const hrs = memberStatus?.cancellationHours ?? null;
    const paid = reg.amountPaid > 0;
    const when = reg.eventDate || product?.eventDate;
    const inside = !!(hrs && when && (new Date(when).getTime() - Date.now()) < hrs * 3600 * 1000);
    // Say what happens to their money BEFORE they confirm, not after.
    const warn = !paid
      ? 'Cancel your registration? Your seat goes back to anyone on the waitlist.'
      : inside
        ? `This is inside the ${hrs}-hour cancellation window, so your payment will not be refunded automatically. Cancel anyway?`
        : 'Cancel your registration? We will refund what you paid.';
    if (!window.confirm(warn)) return;
    setCancelling(true);
    try {
      const r = await cancelMyRegistration(reg.purchaseId);
      toast({
        title: 'Registration cancelled',
        description: r.refundCents > 0
          ? `We've refunded ${formatPrice(r.refundCents)}. It usually lands back on your card within 5-10 business days.`
          : r.insideWindow
            ? "Your seat is released. Reply to your confirmation email if you'd like us to look at a refund."
            : 'Your seat has been released.',
      });
      if (productId) fetchMemberStatus(apiId).then(setMemberStatus).catch(() => {});
      setJoinLink(null);
    } catch (e: any) {
      toast({ title: e?.message || 'Could not cancel your registration', variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };

  // Wire the register link once the product is in and the viewer's state is known.
  useEffect(() => {
    if (!registerFor || !product || registerDone) return;
    if (registerFor.productId !== String(product._id)) return;
    if (!isAuthenticated) {
      setGuestEmail(registerFor.email);
      setGuestMode(true);
      return;
    }
    // Signed in, and as the address the link was sent to: one click was the
    // click on the email. Anyone else signed in gets the page as normal.
    const me = String((user as any)?.email || '').toLowerCase();
    if (me && me === registerFor.email && !joinLink && !checkingOut) { setRegisterDone(true); handleBuy(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFor?.email, product?._id, isAuthenticated, joinLink]);

  const handleShare = async () => {
    const url = window.location.href;
    const name = product?.title || 'Course';
    if (navigator.share) {
      try { await navigator.share({ title: `${name} — Hey Joe Plus`, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied to clipboard' });
    }
  };

  const handleBuy = async () => {
    if (!productId) return;
    // Fire on intent, ahead of the auth gate below. Logged-out visitors get
    // bounced to /auth and many won't come back, but the click still tells Meta
    // who wants to buy — losing that signal would train the campaign on the
    // narrower "already had an account" audience.
    if (product) trackInitiateCheckout(product);
    // Free-event guest with the form already open: this button is the submit.
    if (!isAuthenticated && freeGuestPath && guestMode) { handleGuestCheckout(); return; }
    if (!isAuthenticated) {
      // A paid, open event: ask for an email and go straight to Stripe.
      const publicCharge = activeTier ? activeTier.priceCents : product?.price || 0;
      if (publicCharge > 0 && !access && product?.pricingMode !== 'subscription') { setGuestMode(true); return; }
      // A free event (open, or an agency's invite link): nobody needs an account
      // first. Name + email, the host's questions, and we make the account.
      if (freeGuestPath) {
        setGuestMode(true);
        if ((product?.registrationQuestions || []).length) setAskOpen(true);
        return;
      }
      // What's left needs a real sign-in: agency-only without the invite link,
      // subscriptions, members-only.
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }
    // Creator's questions, asked on the way in. Only where the viewer is actually
    // registering (not on a replay or a second visit) and hasn't already answered.
    // `access` is the agency gate: eligible === false means they can't register at
    // all (they get the early-access form instead, which already asks). Everyone who
    // CAN register and hasn't yet gets the questions here.
    const askQs = (!viewerIsOwner && !joinLink && access?.eligible !== false) ? (product?.registrationQuestions || []) : [];
    if (askQs.length && !askOpen) { setAskOpen(true); return; }
    if (askQs.length && !answersComplete(askQs, qAnswers)) {
      toast({ title: 'One more answer needed', description: 'A required question is blank.' });
      return;
    }
    setCheckingOut(true);
    try {
      const checkoutUrl = await checkoutProduct(apiId, selectedTier || undefined, appliedDiscount?.code, chosenSession || undefined, { useWallet: useWallet && walletCents > 0, ...(askQs.length ? { answers: qAnswers } : {}), ...(access?.viaInvite ? { invite: agencyInviteCode(productId) } : {}) });
      window.location.href = checkoutUrl;
    } catch (err: any) {
      toast({ title: 'Checkout failed', description: err.message, variant: 'destructive' });
      setCheckingOut(false);
    }
  };

  // Seats full → join the waitlist for this occurrence instead of a dead "sold out".
  // Agency-only events: anyone can ask for early access to the creator's next seminars.
  const handleEarlyAccess = async () => {
    if (!productId) return;
    if (!isAuthenticated) { navigate('/auth', { state: { from: location.pathname } }); return; }
    // With questions on the event, the first click opens the short form; the
    // form's own button submits. Without questions it's the one-click list add.
    const qs = product?.registrationQuestions || [];
    if (qs.length && !earlyAccessOpen) { setEarlyAccessOpen(true); return; }
    if (qs.length && !answersComplete(qs, qAnswers)) { toast({ title: 'One more answer needed', description: 'A required question is blank.' }); return; }
    setEarlyAccessState('joining');
    try {
      await requestEarlyAccess(apiId, qs.length ? qAnswers : undefined);
      setEarlyAccessState('joined');
      setEarlyAccessOpen(false);
    } catch (err: any) {
      setEarlyAccessState('idle');
      toast({ title: "Couldn't add you", description: err.message, variant: 'destructive' });
    }
  };
  const handleSubmitAnswers = async () => {
    if (!productId) return;
    const qs = joinLink?.questions || [];
    if (!answersComplete(qs, qAnswers)) { toast({ title: 'One more answer needed', description: 'A required question is blank.' }); return; }
    setQState('saving');
    try {
      await submitRegistrationAnswers(apiId, qAnswers);
      setQState('saved');
      toast({ title: 'Thanks, sent along.' });
    } catch (err: any) {
      setQState('idle');
      toast({ title: "Couldn't save your answers", description: err.message, variant: 'destructive' });
    }
  };

  const handleGuestCheckout = async () => {
    if (!productId) return;
    const email = guestEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setGuestNotice('Enter a valid email address.'); return; }
    // Free event, signed out (open or invite link): name + email + the host's
    // questions travel with the request, and the server makes the account.
    const freeForm = freeGuestPath;
    const freeQs = freeForm ? (product?.registrationQuestions || []) : [];
    if (freeForm && !guestFirst.trim()) { setGuestNotice('Enter your name.'); return; }
    if (freeQs.length && !answersComplete(freeQs, qAnswers)) { setGuestNotice('A required question is blank.'); return; }
    setCheckingOut(true); setGuestNotice(null);
    try {
      const r = await guestCheckout(apiId, email, selectedTier || undefined, appliedDiscount?.code, chosenSession || undefined, registerToken || undefined,
        freeForm ? { ...(access?.viaInvite ? { invite: agencyInviteCode(productId!) } : {}), firstName: guestFirst.trim(), lastName: guestLast.trim(), ...(freeQs.length ? { answers: qAnswers } : {}) } : undefined);
      if (r.checkoutUrl) { window.location.href = r.checkoutUrl; return; }
      if (r.registered) {
        if (product) trackFreeRegistration(product);
        setRegisterDone(true); setHeldByLink(true); setGuestMode(false); setAskOpen(false); setCheckingOut(false); return;
      }
      if (r.member) { setGuestNotice('member'); setCheckingOut(false); return; }
      navigate('/auth', { state: { from: location.pathname } });
    } catch (err: any) {
      setGuestNotice(err.message || 'Could not start checkout.'); setCheckingOut(false);
    }
  };

  const handleWaitlist = async () => {
    if (!productId) return;
    if (!isAuthenticated) { navigate('/auth', { state: { from: location.pathname } }); return; }
    setWaitlistState('joining');
    try {
      const r = await joinWaitlist(apiId);
      setWaitlistState('joined');
      toast({ title: r.alreadyRegistered ? "You're already registered" : r.alreadyOn ? "You're already on the waitlist" : "You're on the waitlist", description: r.alreadyRegistered ? '' : "We'll email you if a spot opens up." });
    } catch {
      setWaitlistState('idle');
      toast({ title: 'Could not join the waitlist', description: 'Please try again.', variant: 'destructive' });
    }
  };

  // ?reg=1 (the store's "Next up" button): one tap to register. A plain event
  // with nothing to choose goes straight into the normal register path; anything
  // with a choice to make (ticket tier, session, age box, host questions) or a
  // viewer who is already in just lands on the register button instead.
  const regIntentHandled = useRef(false);
  useEffect(() => {
    if (regIntentHandled.current || loading || !product) return;
    const qs = new URLSearchParams(location.search);
    if (qs.get('reg') !== '1') return;
    if (isAuthenticated && memberStatus === null) return;   // wait for their status
    regIntentHandled.current = true;
    qs.delete('reg');
    navigate({ pathname: location.pathname, search: qs.toString() ? `?${qs}` : '' }, { replace: true });
    const now = Date.now();
    const futureDates = (product.eventDates || []).filter(d => new Date(d).getTime() > now);
    const needsChoice = (product.ticketTiers?.length ?? 0) > 0
      || (futureDates.length > 1 && !(product as any).attendAllDates)
      || !!product.minAge
      || (product.registrationQuestions || []).length > 0;
    const alreadyIn = !!memberStatus?.myRegistration || !!joinLink?.joinUrl;
    const closed = previewOnly || viewerIsOwner || access?.eligible === false || !!memberStatus?.overCap
      || !product.eventDate || new Date(product.eventDate).getTime() < now;
    setTimeout(() => document.getElementById('register-button')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    if (!needsChoice && !alreadyIn && !closed) handleBuy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, product, memberStatus, isAuthenticated, joinLink]);

  if (postponed) {
    return (
      <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 16px', background: 'var(--hj-ink)' }}>
        <div style={{ maxWidth: 560, background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 'var(--hj-r-lg)', padding: '32px 28px' }}>
          <p className="m-0" style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--hj-beige-ink-3)', marginBottom: 10 }}>Postponed</p>
          <h1 className="m-0" style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2, marginBottom: 14 }}>{postponed.title}</h1>
          <p className="m-0" style={{ fontSize: 15.5, lineHeight: 1.55, marginBottom: 22 }}>{postponed.notice}</p>
          <Link to="/marketplace" style={{ display: 'inline-block', background: 'var(--hj-yellow)', color: 'var(--hj-ink)', textDecoration: 'none', fontWeight: 700, fontSize: 14, padding: '12px 22px', borderRadius: 999 }}>See what's coming up →</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
        <div className="text-center">
          <h1 className="font-hj-display m-0" style={{ fontSize: 28, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}>
            Course not found.
          </h1>
          <p style={{ marginTop: 10, fontSize: 14, color: 'var(--hj-ink)' }}>
            This course may no longer be available.{' '}
            <Link to="/marketplace" style={{ color: 'var(--hj-ink)', textDecoration: 'underline' }}>Browse the marketplace</Link>.
          </p>
        </div>
      </main>
    );
  }

  // Products (a T-shirt, a download) have their own page: photos, options, quantity, checkout.
  if (product.productType === 'goods' && product.goods) {
    return <GoodsProductView product={product} creator={creator ? { displayName: (creator as { displayName?: string }).displayName, slug: (creator as { slug?: string }).slug } : null} previewOnly={previewOnly} />;
  }

  // Preview sources: prefer the dedicated Mux preview clip (public playback id),
  // otherwise fall back to a free-preview S3 video on the product or in a module.
  const muxPreviewReady = product.muxPreview?.muxIngestStatus === 'ready' && !!product.muxPreview?.muxPlaybackId;
  const isSeries = product.productType === 'series' && Array.isArray(product.modules) && product.modules.length > 0;
  // A flat multi-episode video product: format:'series' (or legacy >1 video), no modules.
  const isVideoSeries = !isSeries && (product.format === 'series' || product.videos.length > 1);
  const isWebinar = product.productType === 'webinar';
  const isBundle = product.productType === 'bundle';
  const isPass = isBundle && bundle?.mode === 'pass';
  const bundleClosed = isBundle && !!bundle?.unavailable;
  // Presenter role label: featured events have a "Host", courses/classes an "Instructor".
  // An explicit product.hostRole overrides (the configurable option going forward).
  const roleLabel = (product as any).hostRole || (isWebinar ? 'Host' : 'Instructor');
  // Recurring featured event: every FUTURE session, selectable. The buyer must pick one
  // (mirrors the server's requirement) — defaults to the soonest.
  const sessionOptions = (() => {
    if (!isWebinar || !Array.isArray(product.eventDates) || product.eventDates.length <= 1) {
      return [] as { key: string; full: string; time: string }[];
    }
    const now = Date.now();
    // A session in progress is still registrable — mirrors sessionIsOpen() on the
    // server. Filtering on `t > now` dropped the running session off the picker, so
    // someone arriving at 4:05 for a 4:00 room got defaulted into NEXT week's.
    const openFor = (product.durationMins || 120) * 60000;
    return product.eventDates
      .map(iso => ({ iso, t: new Date(iso).getTime() }))
      .filter(x => isFinite(x.t) && now <= x.t + openFor)
      .sort((a, b) => a.t - b.t)
      .map(x => { const f = formatEventDate(x.iso)!; return { key: x.iso, full: f.full, time: f.time }; });
  })();
  // A cohort program (attendAllDates) lists its dates but never asks the buyer to
  // choose one — enrolling covers all of them. Only genuinely alternative sessions
  // get the picker.
  const isRecurringWebinar = sessionOptions.length > 1 && !(product as any).attendAllDates;
  // Checkout sends chosenSession whenever one exists, not only while the picker
  // shows (sessionOptions > 1): the server demands a date for ANY multi-date
  // product, past dates included, so the LAST session of a run — one future date,
  // no picker — must still name itself or every registration bounces.
  // sessionSeatCap: each session holds N people (a one-person time slot). A full
  // one stays on the picker marked Booked but can't be chosen; the server refuses it too.
  const sessionSeatCap = Number((product as any).sessionSeatCap || 0);
  const sessionFull = (iso: string) => {
    const counts = (product as any).occurrenceCounts;
    if (!(sessionSeatCap > 0) || !counts) return false;
    const t = new Date(iso).getTime();
    const hit = Object.entries(counts).find(([k]) => new Date(k).getTime() === t);
    return !!hit && (hit[1] as number) >= sessionSeatCap;
  };
  const openSessions = sessionOptions.filter(o => !sessionFull(o.key));
  const allSessionsFull = sessionSeatCap > 0 && sessionOptions.length > 0 && openSessions.length === 0;
  const chosenSession = (selectedSession && openSessions.some(o => o.key === selectedSession))
    ? selectedSession
    : (openSessions[0]?.key ?? sessionOptions[0]?.key ?? null);
  // Featured event free for HJ+ members: member deal resolves the charge to $0.
  const memberFree = isWebinar && memberStatus?.memberRate && memberStatus?.chargeCents === 0;
  // Ticket tiers (one-time live events): participant vs observer seats. When
  // present, the picker replaces the single price and checkout charges the
  // selected tier.
  const tiers = product.pricingMode !== 'subscription' && (product.ticketTiers?.length ?? 0) > 0 ? product.ticketTiers! : null;
  const activeTier = tiers ? (tiers.find(t => t.key === selectedTier) || tiers[0]) : null;
  // A free event, signed out: name + email is enough and the server makes the
  // account. Built for the agency invite link (Oct 23 2026); every open free event
  // takes it now (Oct 25 2026), because an ad or a shared link lands signed out
  // and the sign-in wall is where that traffic died (October offer: 125 clicks,
  // nobody through). Not on a register link (the email is already known and the
  // one-tap confirm handles it), subscriptions, members-only, or an agency-only
  // event reached without its invite link.
  const freeGuestPath = !isAuthenticated && !registerFor && !!product
    && product.pricingMode !== 'subscription' && !(product as any).membersOnly
    && (!access || !!access.viaInvite)
    && (activeTier ? activeTier.priceCents : (product.price || 0)) <= 0;
  // The signed-in buyer's resolved deal for a given tier (annual member $0, monthly
  // member $12, non-member $25). Falls back to the tier's list price pre-auth.
  const tierDeal = (key?: string | null) => (key ? memberStatus?.tiers?.find(d => d.key === key) : undefined) || undefined;
  const activeTierDeal = tierDeal(activeTier?.key);
  // What the active tier actually costs THIS buyer (member deal if any, else list).
  const activeTierCharge = activeTier ? (activeTierDeal?.chargeCents ?? activeTier.priceCents) : null;
  const previewVideo: CurriculumItem | undefined = (() => {
    const playable = (v: any) => !!v.url || (v.muxIngestStatus === 'ready' && !!v.muxPlaybackId);
    if (isSeries) {
      for (const m of product.modules!) {
        const found = (m.lessons || []).find(l => l.freePreview && playable(l));
        if (found) return found;
      }
    }
    return product.videos.find(v => v.freePreview && playable(v));
  })();
  // The playback id to hand MuxPlayer: dedicated preview clip first (PUBLIC
  // policy, tokenless), else the free-preview lesson's own asset — which is a
  // SIGNED paid asset and needs playback tokens from
  // /api/mux/playback-token (the endpoint grants them for freePreview
  // videos). Fall back to the raw presigned <video> only when neither works.
  const previewIsLesson = !muxPreviewReady && (previewVideo as any)?.muxIngestStatus === 'ready' && !!(previewVideo as any)?.muxPlaybackId;
  const previewMuxId = muxPreviewReady
    ? product.muxPreview!.muxPlaybackId
    : (previewIsLesson ? (previewVideo as any).muxPlaybackId : null);
  const previewVideoIdx = previewIsLesson
    ? product.videos.findIndex(v => (v as any).s3Key && (v as any).s3Key === (previewVideo as any)?.s3Key && v.freePreview)
    : -1;
  const hasPreview = !!previewMuxId || !!(previewVideo as any)?.url;

  // Fallback studio logos render small+padded; real cover art fills the slot
  // (a 4:3 card in the 16:9 hero letterboxed badly otherwise — MYST launch eve).
  const isLogoCover = !!product.coverImage && /(?:ita-logo|logo)\.(?:png|svg|webp|jpe?g)(?:$|\?)/i.test(product.coverImage);
  const coverSrc = product.coverImage
    ? (product.coverImage.startsWith('http') || product.coverImage.startsWith('/api/') ? product.coverImage : `${STATIC_ROOT}${product.coverImage}`)
    : (product.videos[0]?.thumbnailUrl
      ? (product.videos[0].thumbnailUrl.startsWith('http') ? product.videos[0].thumbnailUrl : `${STATIC_ROOT}${product.videos[0].thumbnailUrl}`)
      : null);

  const totalItems = isSeries
    ? product.modules!.reduce((s, m) => s + (m.lessons?.length || 0), 0)
    : product.videos.length;

  // For a recurring featured event the hero date tracks the chosen session so the big
  // headline and the picker never disagree.
  const ev = formatEventDate(isRecurringWebinar && chosenSession ? chosenSession : product.eventDate);
  // A published event that has no date on purpose. There is nothing to register
  // for, so the page says so plainly and offers the one thing it can: to tell
  // you when there is. `isEvent` still keys the cream event treatment off it —
  // this IS an event, it just hasn't got a date yet.
  const datesTBA = !!(product as any).datesTBA && !product.eventDate;
  const isEvent = !!ev || datesTBA;
  // The guest booked for THIS date, on top of the standing host. A recurring
  // featured event keeps the same hosts every week and rotates the guest, so the two are
  // separate people — the page shows both, and the guest tracks the session the
  // buyer has selected rather than the product's first date.
  const shownDate = (isRecurringWebinar && chosenSession) ? chosenSession : product.eventDate;
  // Everyone presenting a GIVEN date, as people rather than as a string: the
  // standing host plus whoever is booked for that week, each with their billing
  // and their headshot. A weekly show's hosts are the second most load-bearing
  // fact on this page after the date, and they used to render as one 13.5px line
  // six rows down the details list, below "Cancel up to 24 hours before".
  const hostsFor = (iso?: string | null): Presenter[] => {
    const list = (product as any).occurrenceHosts;
    const t = iso ? new Date(iso).getTime() : NaN;
    const hit = isFinite(t) && Array.isArray(list)
      ? list.find((o: any) => o?.eventDate && new Date(o.eventDate).getTime() === t)
      : null;
    // With no product-level host the week's presenters ARE the hosts, so they take
    // the page's own role label rather than a "Guest:" line with nobody above it.
    const fallbackRole = product.instructor ? 'Guest' : roleLabel;
    const occ: Presenter[] = !hit ? []
      : (Array.isArray(hit.hosts) && hit.hosts.length
        // Rows written before headshots existed carry only the one-line credit.
        ? hit.hosts.map((h: any) => ({ name: String(h.name), role: h.role || hit.hostRole || fallbackRole, image: h.image || null }))
        : (hit.instructor ? [{ name: String(hit.instructor), role: hit.hostRole || fallbackRole, image: null }] : []));
    const standing: Presenter[] = product.instructor
      ? [{ name: product.instructor, role: (product as any).hostRole || roleLabel, image: (product as any).instructorImage || null }]
      : [];
    return [...standing, ...occ];
  };
  const shownHosts = hostsFor(shownDate);
  // Partner-fed classes (ITA) carry logistics their own page states plainly and
  // ours buried at the bottom of the description blob: whether it's in the room or
  // on Zoom, whose office, and the cancellation window. Someone booking an in-person
  // LA class from out of state should not have to read to the end to find out.
  // `format` is overloaded — our own products use it for single/series — so only the
  // partner values render.
  const classFormat = (() => {
    const f = String((product as any).format || '');
    if (f === 'in_person') return 'In person';
    if (f === 'online') return 'Online';
    return null;
  })();
  // Where a featured event happens. `locationMode` is absent on everything built
  // before in-person events existed, which reads as online — the same as today.
  const locationMode: string = (product as any).locationMode || 'online';
  const hasVenue = locationMode === 'in_person' || locationMode === 'both';
  const hasOnlineRoom = locationMode !== 'in_person';
  // Per-date venue + paragraph, falling back to the product-level venue. The full
  // street address only arrives in the payload once you're registered, so the
  // public page shows the venue name and city and the buyer's page shows the rest.
  const occurrenceFor = (iso?: string | null) => {
    if (!iso || !Array.isArray((product as any).occurrenceDetails)) return null;
    const t = new Date(iso).getTime();
    return (product as any).occurrenceDetails.find((o: any) => o?.eventDate && new Date(o.eventDate).getTime() === t) || null;
  };
  const venueFor = (iso?: string | null) => {
    const occ = occurrenceFor(iso);
    const base = (product as any).venue || null;
    if (occ && (occ.name || occ.city || occ.address)) return occ;
    return base;
  };
  const venueLineOf = (v: any) => v ? [v.name, v.address, v.city].filter(Boolean).join(', ') : '';
  // Tracks the session the buyer has SELECTED, not the product's headline date —
  // the same rule occGuest follows. On a recurring event the picker is the whole
  // point, so a page that kept showing week one's write-up while you had week
  // three selected would be lying about what you're registering for.
  // Enrolment for the session the buyer has selected. A recurring event's all-time
  // total describes a room nobody is joining — with Friday picked, "76 enrolled" is
  // every Waiting Room ever, not Friday's. Falls back to the all-time count for a
  // one-off, which is the only thing it can mean there.
  const enrolledFor = (iso?: string | null) => {
    const counts = (product as any).occurrenceCounts;
    if (!counts || !iso) return 0;
    const t = new Date(iso).getTime();
    const hit = Object.entries(counts).find(([k]) => new Date(k).getTime() === t);
    return hit ? (hit[1] as number) : 0;
  };
  // Just the week's own presenters, for the session picker — the standing host is
  // the same on every row there and repeating them tells you nothing.
  const occHostsFor = (iso?: string | null): Presenter[] => {
    const list = (product as any).occurrenceHosts;
    const t = iso ? new Date(iso).getTime() : NaN;
    if (!isFinite(t) || !Array.isArray(list)) return [];
    const hit = list.find((o: any) => o?.eventDate && new Date(o.eventDate).getTime() === t);
    if (!hit) return [];
    if (Array.isArray(hit.hosts) && hit.hosts.length) {
      return hit.hosts.map((h: any) => ({ name: String(h.name), role: h.role || null, image: h.image || null }));
    }
    return hit.instructor ? [{ name: String(hit.instructor), role: hit.hostRole || null, image: null }] : [];
  };
  const shownEnrolled = (() => {
    // A creator can hide the tally on their own listing. An early, honest number
    // is the one thing on the page that argues against buying, and a low count
    // on a good event reads as a verdict rather than a start.
    if ((product as any).hideEnrolledCount || sessionSeatCap > 0) return 0;
    const counts = (product as any).occurrenceCounts;
    if (!isRecurringWebinar || !counts || !shownDate) return product.memberCount ?? 0;
    const t = new Date(shownDate).getTime();
    const hit = Object.entries(counts).find(([iso]) => new Date(iso).getTime() === t);
    return hit ? (hit[1] as number) : 0;
  })();
  const headlineVenue = venueFor(shownDate);
  const headlineDetails = occurrenceFor(shownDate)?.details || null;
  const instructorOffice = (product as any).instructorOffice || null;
  const cancellationHours = typeof (product as any).cancellationHours === 'number'
    ? (product as any).cancellationHours : null;

  // The event already ended (last occurrence + duration + 1h grace, mirroring
  // the checkout guard) — registration closes, button reads "This event has ended".
  const eventEnded = (() => {
    if (!product.eventDate) return false;
    const occ = (product.eventDates?.length ? product.eventDates : [product.eventDate])
      .map(d => new Date(d).getTime()).filter(t => isFinite(t));
    if (!occ.length) return false;
    return Date.now() > Math.max(...occ) + ((product.durationMins || 120) + 60) * 60000;
  })();
  // A finished featured event that has a recording is a REPLAY: still worth selling,
  // just not as a seat. It stays purchasable and the page sells the recording.
  // A finished featured event with nothing recorded has nothing to sell, so it keeps
  // the closed treatment above.
  const hasReplay = isWebinar && (product.videos || []).some(v => v.muxPlaybackId || v.s3Key);
  const isReplay = eventEnded && hasReplay;
  // Active replay: the host answers replay questions within 24 hours for N days
  // after the last session. Before the event it's a reason to register even if
  // you can't come live; after, it's the window that's still open. Same rule as
  // the server's activeReplayWindow(): last session to last session + days.
  const activeReplay = (() => {
    const ar = product.activeReplay;
    if (!isWebinar || !ar?.enabled) return null;
    const dates = [...(product.eventDates || []), product.eventDate].map(d => d ? new Date(d).getTime() : NaN).filter(t => isFinite(t));
    if (!dates.length) return null;
    const last = Math.max(...dates);
    const until = last + (ar.days || 7) * 86400000;
    const now = Date.now();
    if (now >= until) return null;
    const hostFirst = String((product as any).instructor || shownHosts[0]?.name || '').trim().split(/\s+/)[0] || 'The host';
    return { live: now >= last, days: ar.days || 7, untilLabel: new Date(until).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }), hostFirst };
  })();
  const activeReplayNote = activeReplay && (
    <div className="flex items-start gap-2" style={{ marginTop: 10, fontSize: 13, lineHeight: 1.45, color: isEvent ? 'var(--hj-beige-ink)' : 'var(--hj-ink)' }}>
      <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" style={{ marginTop: 2, color: isEvent ? 'var(--hj-beige-ink-2)' : 'var(--hj-ink-3)' }} />
      <span>
        <strong>Active replay{activeReplay.live ? ` until ${activeReplay.untilLabel}` : ''}.</strong>{' '}
        {activeReplay.live
          ? `Ask ${activeReplay.hostFirst} anything about the replay and get an answer within 24 hours.`
          : `Can't make it live? Watch the recording and ask ${activeReplay.hostFirst} anything for ${activeReplay.days} days after. Every question is answered within 24 hours.`}
      </span>
    </div>
  );
  // Late-join price (server sweep lowers product.price as sessions go by): say why
  // it's lower, and point to the replay when there is one.
  const cohortLeft = (() => {
    if (!isWebinar || !(product as any).attendAllDates || !Array.isArray(product.eventDates) || product.eventDates.length <= 1) return null;
    const ts = product.eventDates.map(d => new Date(d).getTime()).filter(t => isFinite(t));
    const left = ts.filter(t => t > Date.now()).length;
    return left > 0 ? { left, total: ts.length, started: left < ts.length } : null;
  })();
  const lateJoin = (() => {
    const lj = (product as any).lateJoinDiscount;
    if (!isWebinar || isReplay || !lj?.enabled || !(lj.percentOff > 0) || !(lj.fullPrice > product.price)) return null;
    return { fullPrice: lj.fullPrice as number, percentOff: lj.percentOff as number, missed: lj.sessionsMissed as number, total: lj.sessionsTotal as number };
  })();
  // A cohort that has started: say how many classes are left, not how many it had.
  const leftLine = cohortLeft?.started ? (cohortLeft.left === 1 ? "There's only one more class left." : `${cohortLeft.left} classes left.`) : null;
  const lateJoinNote = lateJoin ? (
    <div className="flex items-start gap-2" style={{ marginTop: 10, fontSize: 13, lineHeight: 1.45, color: isEvent ? 'var(--hj-beige-ink)' : 'var(--hj-ink)' }}>
      <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" style={{ marginTop: 2, color: isEvent ? 'var(--hj-beige-ink-2)' : 'var(--hj-ink-3)' }} />
      <span>
        <strong>Joining late, {lateJoin.percentOff}% off.</strong>{' '}
        {leftLine
          ? `${leftLine.replace(/\.$/, '')}, so the price is ${formatPrice(product.price)} instead of ${formatPrice(lateJoin.fullPrice)}.`
          : `${lateJoin.missed} of ${lateJoin.total} sessions ${lateJoin.missed === 1 ? 'has' : 'have'} already happened, so the price is ${formatPrice(product.price)} instead of ${formatPrice(lateJoin.fullPrice)}.`}
        {hasReplay ? ' The replay is included.' : ''}
      </span>
    </div>
  ) : leftLine && !isReplay ? (
    <div className="flex items-start gap-2" style={{ marginTop: 10, fontSize: 13, lineHeight: 1.45, fontWeight: 600, color: isEvent ? 'var(--hj-beige-ink)' : 'var(--hj-ink)' }}>
      <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" style={{ marginTop: 2, color: isEvent ? 'var(--hj-beige-ink-2)' : 'var(--hj-ink-3)' }} />
      <span>{leftLine}</span>
    </div>
  ) : null;
  // A recurring show's rail has to name the date it belongs to — the picker under
  // it swaps the faces out, and a bare "Your hosts" over week three's lineup while
  // week one is selected is just wrong. Short form; `.mono-label` uppercases it.
  const hostRailLabel = isRecurringWebinar && ev
    ? `Hosting ${ev.month} ${ev.day}`
    : isReplay ? 'Hosted by'
      : `Your ${isWebinar ? 'host' : 'instructor'}${shownHosts.length > 1 ? 's' : ''}`;
  // What this product calls itself. "Webinar" is right for a one-hour talk and wrong
  // for a two-month cohort, so a product can override the noun without inventing a new
  // productType (which every switch in the app would then have to learn).
  const formatNoun: string = (product as any).formatLabel
    || (isEvent ? (isWebinar ? 'Featured event' : 'Class') : isBundle ? (isPass ? 'Season pass' : 'Bundle') : product.pricingMode === 'subscription' ? 'Subscription' : isSeries ? 'Course' : isVideoSeries ? 'Series' : 'Product');
  const formatLabel: string = isReplay ? `${formatNoun} replay` : formatNoun;

  // Additional FUTURE occurrences beyond the headline (next) date — powers the
  // "More sessions" block for multi-date featured events. Sorted, excludes the headline.
  const moreSessions = (() => {
    if (!Array.isArray(product.eventDates) || product.eventDates.length <= 1) return [] as { key: string; full: string; time: string }[];
    const now = Date.now();
    const headlineTs = product.eventDate ? new Date(product.eventDate).getTime() : null;
    // For a cohort the schedule IS every session, so the headline date belongs in the
    // list. Excluding it made a four-Zoom program advertise three, which reads as
    // "and three more" rather than "here is the whole thing you are buying".
    const keepHeadline = !!(product as any).attendAllDates;
    return product.eventDates
      .map(iso => ({ iso, t: new Date(iso).getTime() }))
      .filter(x => isFinite(x.t) && x.t > now && (keepHeadline || x.t !== headlineTs))
      .sort((a, b) => a.t - b.t)
      .map(x => {
        const f = formatEventDate(x.iso)!;
        return { key: x.iso, full: f.full, time: f.time };
      });
  })();

  const avatarUrl = creator?.imageUrl
    ? (creator.imageUrl.startsWith('http') || creator.imageUrl.startsWith('/api/') ? creator.imageUrl : `${STATIC_ROOT}${creator.imageUrl}`)
    : null;
  // Testimonials heading follows the endorsers' role — "What directors say",
  // "What casting directors say", etc. — instead of a fixed "What casting says".
  const testimonialHeading = (() => {
    const titles = (product.testimonials || []).map(t => (t.title || '').trim()).filter(Boolean);
    if (titles.length && titles.every(t => t.toLowerCase() === titles[0].toLowerCase())) {
      const role = titles[0];
      return `What ${(/s$/i.test(role) ? role : `${role}s`).toLowerCase()} say`;
    }
    return 'What people say';
  })();

  const primaryPill: React.CSSProperties = {
    padding: '12px 22px',
    borderRadius: 999,
    background: 'var(--hj-blue)',
    border: '1px solid var(--hj-blue)',
    color: 'oklch(0.18 0.005 70)',
    fontSize: 14,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    minHeight: 48,
  };

  const renderItemRow = (item: CurriculumItem, idx: number) => {
    const ft = (item as any).fileType || 'video';
    const isFree = !!item.freePreview;
    const epNum = (item as any).episodeNumber ?? idx + 1;
    const logline = (item as any).logline as string | undefined;
    const locked = !!(item as any).locked;
    const availableDate = (item as any).availableDate as string | undefined;
    const relDate = availableDate
      ? new Date(availableDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : null;
    // Public curriculum shows only how many chapter files a lesson carries — no
    // titles or links. `attachmentCount` from /public, `attachments` as a fallback.
    const attachCount = (item as any).attachmentCount ?? (item as any).attachments?.length ?? 0;
    return (
      <div
        key={(item as any).id || (item as any).s3Key || idx}
        className="flex items-center gap-3"
        style={{ padding: '11px 14px', borderTop: idx === 0 ? 'none' : '1px solid var(--hj-hairline-soft)' }}
      >
        <span className="tabular flex-shrink-0" style={{ fontSize: 12, color: 'var(--hj-ink-4)', minWidth: 20, textAlign: 'center' }}>
          {isVideoSeries ? `Ep ${epNum}` : idx + 1}
        </span>
        {fileIconFor(ft)}
        <span className="flex-1 min-w-0">
          <span className="block truncate" style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>
            {(item as any).title || `Item ${idx + 1}`}
          </span>
          {logline && (
            <span className="block truncate" style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 1 }}>{logline}</span>
          )}
        </span>
        {attachCount > 0 && (
          <span
            className="tabular inline-flex items-center gap-1 flex-shrink-0"
            title={`${attachCount} attachment${attachCount === 1 ? '' : 's'}`}
            style={{ fontSize: 11, color: 'var(--hj-ink-3)', fontFamily: 'var(--hj-font-mono)' }}
          >
            <Paperclip className="w-3 h-3" /> {attachCount}
          </span>
        )}
        {locked ? (
          <span className="inline-flex items-center gap-1 flex-shrink-0" style={{ fontSize: 11.5, color: 'var(--hj-ink-3)', fontWeight: 600 }}>
            <Lock className="w-3.5 h-3.5" /> {relDate ? `Drops ${relDate}` : 'Coming soon'}
          </span>
        ) : isFree ? (
          <span className="inline-flex items-center gap-1 flex-shrink-0" style={{ fontSize: 11.5, color: 'var(--hj-yellow-ink)', fontWeight: 600 }}>
            <Eye className="w-3.5 h-3.5" /> Free preview
          </span>
        ) : (
          <Lock className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-4)', flexShrink: 0 }} />
        )}
      </div>
    );
  };

  // Creators paste links, bold, bullets and the odd HTML tag into descriptions.
  // renderRichText shows them as formatting (src/lib/richText.tsx), never as raw HTML.
  const renderEmphasis = (text: string) => renderRichText(text);

  // Price after any applied discount code — display only; the server recomputes.
  const withCode = (cents: number) => Math.max(0, cents - (appliedDiscount?.amountOffCents || 0));

  // Members-only event and a viewer who isn't a member: signed out, or signed in
  // with member status back and not Plus. While status loads, the normal button
  // shows; the server refuses non-members either way.
  const membersOnlyLocked = !!(product as any).membersOnly && !viewerIsOwner && !eventEnded
    && (!isAuthenticated || (!!memberStatus && !memberStatus.isPlusMember));

  // Register CTA (buy/register button + the featured event "join Plus & attend free" upsell),
  // reused in the right purchase panel and under the left Class-details panel.
  // Registered featured event buyers see the Join link instead of the buy button.
  const watchDates = async () => {
    if (!isAuthenticated) { navigate('/auth', { state: { from: location.pathname } }); return; }
    setWatchState('joining');
    try {
      const r = await fetch(`/api/creator/products/${productId}/notify-dates`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (!r.ok) throw new Error('Could not add you to the list');
      setWatchState('joined');
    } catch {
      setWatchState('idle');
      toast({ title: 'Could not add you to the list', description: 'Give it another go in a moment.', variant: 'destructive' });
    }
  };

  // The October offer, under the buy button: only while it is open, only to someone
  // the server says has no paid Plus, only on a paid listing they can actually buy.
  const offerNudge = () => (
    octOffer?.open && (octOffer.kind === 'new' || octOffer.kind === 'signed_out')
      && !product.inquiry?.enabled && (activeTier ? activeTier.priceCents : product.price) > 0 ? (
      <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)' }}>
        <p className="m-0" style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)', lineHeight: 1.4 }}>{offerPageHeadline(octOffer)} {offerScarcityLine(octOffer)}</p>
        <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink)', lineHeight: 1.5, marginTop: 4 }}>
          Use it on this ticket.{' '}
          <Link to={`/offer?returnTo=${encodeURIComponent(location.pathname)}`} style={{ color: 'var(--hj-ink)', fontWeight: 700 }}>See the offer →</Link>
        </p>
      </div>
    ) : null
  );

  // Interview-only classes (product.inquiry): a request form replaces checkout.
  const registerCta = (slot: 'details' | 'side' = 'side') => product.inquiry?.enabled ? (
    <InquiryForm
      productId={productId!}
      buttonLabel={product.inquiry.buttonLabel || 'Book an interview'}
      buttonStyle={primaryPill}
      studioName={creator?.displayName || 'The studio'}
      defaults={{ email: user?.email || '' }}
    />
  ) : (
    datesTBA ? (
      <>
        <button
          type="button"
          onClick={watchDates}
          disabled={watchState !== 'idle'}
          className="w-full inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          style={{ ...primaryPill, opacity: watchState === 'joining' ? 0.6 : 1,
                   background: watchState === 'joined' ? 'var(--hj-surface-2)' : undefined,
                   color: watchState === 'joined' ? 'var(--hj-ink)' : undefined }}
        >
          {watchState === 'joining' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
          {watchState === 'joined' ? "You're on the list" : 'Tell me when dates are set'}
        </button>
        <p style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink-2)', textAlign: 'center', lineHeight: 1.45 }}>
          {watchState === 'joined'
            ? `${creator?.displayName || 'The host'} will let you know as soon as this is scheduled.`
            : 'No charge, and nothing is booked. You just hear about it first.'}
        </p>
      </>
    ) :
    // Ended event + entitled viewer (join link resolves only for registrants,
    // creators, and admins): the join link is dead — point at the replay.
    eventEnded && joinLink?.joinUrl ? (
      <>
        <button
          type="button"
          onClick={() => navigate(`/purchases/${productId}`)}
          className="w-full inline-flex items-center justify-center gap-2"
          style={primaryPill}
        >
          <Play className="w-4 h-4" /> Watch the replay →
        </button>
        <p style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink)', textAlign: 'center' }}>
          You attended this event — the replay is available for two weeks.
        </p>
      </>
    ) : (joinLink?.joinUrl && !isRecurringWebinar) ? (
      <>
        <a
          href={joinLink.joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ ...primaryPill, textDecoration: 'none' }}
        >
          <Video className="w-4 h-4" /> {(product as any).joinLabel || `Join the ${formatNoun.toLowerCase()}`} →
        </a>
        <p style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink)', textAlign: 'center' }}>
          {viewerIsOwner
            ? 'Host access — you can join as the creator or an admin. Buyers see this link once they register.'
            : (product as any).joinLabel ? "You're registered." : "You're registered — the room opens at start time."}
        </p>
        {!viewerIsOwner && memberStatus?.myRegistration && !eventEnded && (
          <p style={{ marginTop: 6, fontSize: 12.5, textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleCancelRegistration}
              disabled={cancelling}
              style={{ background: 'none', border: 'none', padding: 0, cursor: cancelling ? 'default' : 'pointer',
                       color: 'var(--hj-ink-3)', textDecoration: 'underline', fontSize: 12.5 }}
            >
              {cancelling ? 'Cancelling…' : "Can't make it? Cancel my registration"}
            </button>
          </p>
        )}
      </>
    ) : membersOnlyLocked ? (
      // Members-only event (product.membersOnly): no seat to buy. A non-member gets
      // the way in, which is Plus; ?p= stamps the signup with this event.
      <>
        <a
          href={`/plans?c=webinar-joinplus&p=${productId}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ ...primaryPill, textDecoration: 'none' }}
        >
          Join Hey Joe Plus to attend →
        </a>
        <p style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink)', textAlign: 'center', lineHeight: 1.45 }}>
          This event is exclusive to Hey Joe Plus members, and free for them.
          {!isAuthenticated && (
            <>
              {' '}Already a member?{' '}
              <Link to="/auth" state={{ from: location.pathname }} style={{ color: 'var(--hj-ink)', fontWeight: 700 }}>Sign in</Link>
            </>
          )}
        </p>
      </>
    ) : (
    <>
      {/* Recurring featured event + already registered for a session: keep the Join
          link handy but still let them register for additional sessions. */}
      {isRecurringWebinar && joinLink?.joinUrl && (
        <a
          href={joinLink.joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ ...primaryPill, textDecoration: 'none', marginBottom: 10 }}
        >
          <Video className="w-4 h-4" /> {(product as any).joinLabel || 'Join your session'} →
        </a>
      )}
      {/* Age attestation. Set `minAge` on a product and registration requires ticking
          this first. It's an attestation, not a verification: 27% of talent profiles
          carry no date of birth, and minors are the least likely to have completed one,
          so a DOB check would block the wrong people and miss the right ones. This is
          for sessions that are candid industry talk rather than anything unsuitable. */}
      {!!product.minAge && !eventEnded && (
        <label
          className="flex items-start gap-2.5"
          style={{ marginBottom: 10, fontSize: 13.5, lineHeight: 1.45, color: 'var(--hj-ink)', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={ageConfirmed}
            onChange={(e) => setAgeConfirmed(e.target.checked)}
            style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
          />
          <span>I confirm I am {product.minAge} or older.</span>
        </label>
      )}
      {isBundle && bundle && (bundle.unavailable ? (
        <p className="m-0" role="status" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5, marginBottom: 10, fontWeight: 600 }}>
          {bundle.unavailable}
        </p>
      ) : bundle.saveCents > 0 ? (
        <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5, marginBottom: 10 }}>
          <strong>Save {formatPrice(bundle.saveCents)}</strong> vs. buying {isPass ? 'each event' : 'each one'} separately ({formatPrice(bundle.separateCents)}).
        </p>
      ) : null)}
      {access && (
        <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink)', lineHeight: 1.5, marginBottom: 8 }}>
          {access.eligible === false
            ? `This event is only open to talent represented by ${access.restrictedTo}. If you are with ${access.restrictedTo}, sign in with the email on your agency profile.`
            : access.viaInvite
              ? `Open to talent represented by ${access.restrictedTo}. You came in on ${access.restrictedTo}'s invite link, so registering also adds you to their roster on Hey Joe.`
              : `Open to talent represented by ${access.restrictedTo}.`}
        </p>
      )}
      {autoEnroll.length > 0 && !memberStatus?.myRegistration && !joinLink && !eventEnded && !guestSuccess && (
        <div className="flex items-start gap-2" style={{ marginBottom: 10, padding: '10px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)' }}>
          <span style={{ fontSize: 15, lineHeight: 1.3 }}>🎁</span>
          <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink)', lineHeight: 1.45 }}>
            <strong>Included:</strong> registering also enrolls you, free, in {autoEnroll.map(t => `“${t}”`).join(' and ')}.
            {autoEnrollTaglines.map(t => ` ${/[.!?]$/.test(t) ? t : `${t}.`}`).join('')}
          </p>
        </div>
      )}
      {/* The host's questions, asked BEFORE the seat is taken. Opened by the first
          click on the register button; that same button then submits and registers. */}
      {/* Free-event guest (open or invite link): name and email sit with the
          questions, above the one register button, which submits them. */}
      {ctaSlot === slot && freeGuestPath && guestMode && !guestSuccess && (
        <div style={{ marginTop: 10, marginBottom: 10, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', display: 'grid', gap: 8 }}>
          <p className="m-0" style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)' }}>Your name and email</p>
          <div className="flex gap-2">
            {[[guestFirst, setGuestFirst, 'First name'], [guestLast, setGuestLast, 'Last name']].map(([v, set, ph]: any) => (
              <input key={ph} type="text" value={v} onChange={e => { set(e.target.value); if (guestNotice) setGuestNotice(null); }} placeholder={ph} autoComplete={ph === 'First name' ? 'given-name' : 'family-name'}
                style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 999, border: '1px solid var(--hj-hairline)', fontSize: 14, fontFamily: 'inherit', background: 'var(--hj-surface-1)', color: 'var(--hj-ink)' }} />
            ))}
          </div>
          <input type="email" value={guestEmail} onChange={e => { setGuestEmail(e.target.value); if (guestNotice) setGuestNotice(null); }} placeholder="you@example.com" autoComplete="email"
            style={{ padding: '10px 12px', borderRadius: 999, border: '1px solid var(--hj-hairline)', fontSize: 14, fontFamily: 'inherit', background: 'var(--hj-surface-1)', color: 'var(--hj-ink)' }} />
          <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink-2)' }}>We'll send your confirmation and join link here.</p>
          {guestNotice && <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink)', fontWeight: 600 }}>{guestNotice}</p>}
        </div>
      )}
      {ctaSlot === slot && askOpen && !joinLink && (product.registrationQuestions || []).length > 0 && access?.eligible !== false && (
        <div style={{ marginTop: 10, marginBottom: 10, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
          <p className="m-0" style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: 12 }}>
            {product.instructor ? `Before we save your spot, ${product.instructor} has a couple of quick questions` : 'Before we save your spot, a couple of quick questions'}
          </p>
          <RegistrationQuestionsForm questions={product.registrationQuestions || []} answers={qAnswers} onChange={setQAnswers} />
        </div>
      )}
      <button
        id="register-button"
        type="button"
        onClick={() => { setCtaSlot(slot); handleBuy(); }}
        disabled={guestSuccess || (previewOnly && !viewerIsOwner) || access?.eligible === false || !!memberStatus?.overCap || checkingOut || !!activeTier?.soldOut || allSessionsFull || (eventEnded && !isReplay) || (!!product.minAge && !ageConfirmed && !eventEnded) || bundleClosed}
        className="w-full inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed"
        style={{ ...primaryPill, opacity: checkingOut || activeTier?.soldOut || allSessionsFull || (eventEnded && !isReplay) || (!!product.minAge && !ageConfirmed && !eventEnded) || bundleClosed ? 0.6 : 1 }}
      >
        {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
        {guestSuccess ? "You're registered"
          : allSessionsFull && !eventEnded ? 'Every time is booked'
          : previewOnly && !viewerIsOwner ? 'Not open yet'
          : bundleClosed ? 'Not available right now'
          : access?.eligible === false ? `For ${access.restrictedTo} clients only`
          : askOpen ? 'Save my spot →'
          : memberStatus?.overCap ? 'Member-rate class already used'
          : eventEnded && !isReplay ? 'This event has ended'
          : !isAuthenticated
          ? ((activeTier ? activeTier.priceCents : product.price) > 0 && !access && product.pricingMode !== 'subscription'
              // withCode, like every signed-in branch below: a club link lands here
              // signed out, and a button still reading the full price is the last
              // thing a visitor sees before deciding the code did nothing.
              ? `${isReplay ? 'Watch the replay' : isWebinar ? 'Register' : 'Buy'} – ${withCode(activeTier ? activeTier.priceCents : product.price) === 0 ? 'Free' : formatPrice(withCode(activeTier ? activeTier.priceCents : product.price))}`
              : freeGuestPath ? 'Register – Free'
              : (isReplay ? 'Sign in to watch' : isWebinar ? 'Sign in to register' : product.pricingMode === 'subscription' ? (product.price > 0 ? 'Sign in to subscribe' : 'Sign in to join free') : 'Sign in to buy'))
          : product.pricingMode === 'subscription' ? (product.price > 0 ? 'Subscribe' : 'Join free')
          : activeTier ? (activeTier.soldOut ? (activeTier.priceCents === 0 ? 'Full' : 'Sold out')
            : activeTierDeal?.annualFreeClass ? `${isReplay ? 'Watch the replay' : isWebinar ? 'Register' : 'Buy'} free – your annual class`
            : `${isReplay ? 'Watch the replay' : isWebinar ? 'Register' : 'Buy'} – ${withCode(activeTierCharge!) === 0 ? 'Free' : formatPrice(withCode(activeTierCharge!))}`)
          : isWebinar ? (
            isReplay
              ? (memberFree ? 'Watch free' : `Watch the replay – ${withCode(memberStatus?.chargeCents ?? product.price) === 0 ? 'Free' : formatPrice(withCode(memberStatus?.chargeCents ?? product.price))}`)
              : (memberFree ? 'Register free' : `Register – ${withCode(memberStatus?.chargeCents ?? product.price) === 0 ? 'Free' : formatPrice(withCode(memberStatus?.chargeCents ?? product.price))}`))
          : 'Buy now'}
      </button>
      {/* Same escape hatch on the branch with no join link: plenty of events never
          resolve one, and being registered with no way out is the whole complaint. */}
      {!viewerIsOwner && memberStatus?.myRegistration && !eventEnded && (
        <p style={{ marginTop: 8, fontSize: 12.5, textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleCancelRegistration}
            disabled={cancelling}
            style={{ background: 'none', border: 'none', padding: 0, cursor: cancelling ? 'default' : 'pointer',
                     color: 'var(--hj-ink-3)', textDecoration: 'underline', fontSize: 12.5 }}
          >
            {cancelling ? 'Cancelling…' : "Can't make it? Cancel my registration"}
          </button>
        </p>
      )}
      {guestSuccess && (
        <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)' }}>
          <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
            <strong>You're registered.</strong> Your confirmation and the join link are on their way to the email you gave at checkout. Sign in with that email any time to see it here.
          </p>
        </div>
      )}
      {guestSuccess && <NextEventCrossSell excludeProductId={productId} />}
      {ctaSlot === slot && !isAuthenticated && guestMode && !freeGuestPath && (
        <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}>
          {guestNotice === 'member' ? (
            <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
              That email has a Hey Joe Plus membership, so you get the member price.{' '}
              <Link to="/auth" state={{ from: location.pathname }} style={{ color: 'var(--hj-ink)', fontWeight: 700 }}>Sign in to register</Link>
            </p>
          ) : (
            <>
              <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink)', marginBottom: 8 }}>
                {registerFor ? 'Confirm your seat and we\u2019ll send the join link to:' : 'Where should we send your confirmation and join link?'}
              </p>
              <div className="flex gap-2">
                <input type="email" value={guestEmail} readOnly={!!registerFor} onChange={e => { setGuestEmail(e.target.value); if (guestNotice) setGuestNotice(null); }} placeholder="you@example.com" autoFocus={!registerFor}
                  onKeyDown={e => { if (e.key === 'Enter') handleGuestCheckout(); }}
                  style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 999, border: '1px solid var(--hj-hairline)', fontSize: 14, fontFamily: 'inherit', background: registerFor ? 'var(--hj-surface-2)' : 'var(--hj-surface-1)', color: 'var(--hj-ink)' }} />
                <button type="button" onClick={handleGuestCheckout} disabled={checkingOut}
                  style={{ ...primaryPill, width: 'auto', padding: '10px 16px', fontSize: 14 }}>
                  {checkingOut ? 'One sec…' : registerFor ? 'Confirm my seat' : 'Continue'}
                </button>
              </div>
              {guestNotice && <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink)', marginTop: 8 }}>{guestNotice}</p>}
            </>
          )}
        </div>
      )}
      {freeGuestPath && !guestSuccess && (
        <p className="m-0" style={{ marginTop: 8, fontSize: 12.5, textAlign: 'center' }}>
          <Link to="/auth" state={{ from: location.pathname }} style={{ color: 'var(--hj-ink)', fontWeight: 700 }}>Have a Hey Joe account? Sign in instead</Link>
        </p>
      )}
      {!isAuthenticated && !access && (activeTier ? activeTier.priceCents : product.price) > 0 && product.pricingMode !== 'subscription' && (
        <p className="m-0" style={{ marginTop: 8, fontSize: 12.5, textAlign: 'center' }}>
          <Link to="/auth" state={{ from: location.pathname }} style={{ color: 'var(--hj-ink)', fontWeight: 700 }}>Plus member? Sign in for member pricing</Link>
        </p>
      )}
      {/* Files that come with registering (slides, a worksheet): signed links on the
          join payload, so only registrants, the creator and admins see them. */}
      {ctaSlot === slot && joinLink && (joinLink.downloads || []).length > 0 && (
        <div style={{ marginTop: 14, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
          <p className="m-0" style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: 10 }}>Yours to download</p>
          <ul className="m-0 p-0" style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(joinLink.downloads || []).map((d) => (
              <li key={d.id}>
                <a href={d.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2" style={{ color: 'var(--hj-ink)', fontWeight: 600, fontSize: 14, textDecoration: 'underline' }}>
                  <FileText className="w-4 h-4" /> {d.title}{d.bytes ? <span style={{ fontWeight: 400, color: 'var(--hj-ink-2)' }}> · {(d.bytes / 1048576).toFixed(1)} MB</span> : null}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Registered viewer + creator questions: a short card, once. Also the landing
          spot for the "Answer them here" link in the confirmation email (?ask=1). */}
      {ctaSlot === slot && joinLink && !viewerIsOwner && (joinLink.questions || []).length > 0 && !joinLink.answers && qState !== 'saved' && (
        <div id="questions" style={{ marginTop: 14, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: `1px solid ${askedFromEmail ? 'var(--hj-yellow)' : 'var(--hj-hairline)'}` }}>
          <p className="m-0" style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: 12 }}>
            {product.instructor ? `${product.instructor} has a couple of quick questions for you` : 'A couple of quick questions from the host'}
          </p>
          <RegistrationQuestionsForm questions={joinLink.questions || []} answers={qAnswers} onChange={setQAnswers} />
          <button type="button" onClick={handleSubmitAnswers} disabled={qState === 'saving'} className="w-full inline-flex items-center justify-center"
            style={{ marginTop: 14, padding: '11px 16px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', border: 0, fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', opacity: qState === 'saving' ? 0.6 : 1 }}>
            {qState === 'saving' ? 'Sending…' : 'Send my answers'}
          </button>
        </div>
      )}
      {/* Agency-only event: a second button so everyone else can still raise a hand. */}
      {ctaSlot === slot && access && !eventEnded && earlyAccessOpen && earlyAccessState !== 'joined' && (product.registrationQuestions || []).length > 0 && (
        <div style={{ marginTop: 10, padding: 16, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
          <p className="m-0" style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: 12 }}>Before we add you, a couple of quick questions</p>
          <RegistrationQuestionsForm questions={product.registrationQuestions || []} answers={qAnswers} onChange={setQAnswers} />
        </div>
      )}
      {access && !access.viaInvite && !eventEnded && (
        <button
          type="button"
          onClick={() => { setCtaSlot(slot); handleEarlyAccess(); }}
          disabled={earlyAccessState === 'joining' || earlyAccessState === 'joined'}
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ marginTop: 10, padding: '12px 16px', borderRadius: 999,
            background: earlyAccessState === 'joined' ? 'var(--hj-surface-2)' : 'var(--hj-yellow)',
            color: earlyAccessState === 'joined' ? 'var(--hj-ink)' : 'oklch(0.18 0.005 70)',
            border: '1px solid var(--hj-yellow)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
            cursor: earlyAccessState === 'joined' ? 'default' : 'pointer' }}
        >
          {earlyAccessState === 'joining' ? 'Adding you…'
            : earlyAccessState === 'joined' ? "✓ You're on the early access list"
            : !isAuthenticated ? 'Sign in for early access to upcoming seminars'
            : earlyAccessOpen ? 'Add me to the early access list →'
            : 'Get early access to upcoming seminars →'}
        </button>
      )}
      {/* Seats full → waitlist (exclusive-seat model): capture interest, no dead end. */}
      {isWebinar && !!activeTier?.soldOut && !eventEnded && (
        <button
          type="button"
          onClick={handleWaitlist}
          disabled={waitlistState === 'joining' || waitlistState === 'joined'}
          className="w-full inline-flex items-center justify-center gap-2"
          style={{ marginTop: 10, padding: '12px 16px', borderRadius: 999,
            background: waitlistState === 'joined' ? 'var(--hj-surface-2)' : 'var(--hj-yellow)',
            color: waitlistState === 'joined' ? 'var(--hj-ink)' : 'oklch(0.18 0.005 70)',
            border: '1px solid var(--hj-yellow)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
            cursor: waitlistState === 'joined' ? 'default' : 'pointer' }}
        >
          {waitlistState === 'joining' ? 'Adding you…'
            : waitlistState === 'joined' ? "✓ You're on the waitlist — we'll email you if a spot opens"
            : 'Join the waitlist →'}
        </button>
      )}
      {(product as any).plusOfferMonths > 0 && (
        <div className="flex items-start gap-2" style={{ marginTop: 10, padding: '10px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-yellow)' }}>
          <span style={{ fontSize: 15, lineHeight: 1 }}>🎁</span>
          <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink)', lineHeight: 1.45 }}>
            <strong>Includes {(product as any).plusOfferMonths} months of Hey Joe Plus free.</strong>{' '}
            Buy the class, then join with the promo code on your purchase page — billing starts month {(product as any).plusOfferMonths + 1}.
          </p>
        </div>
      )}
      {/* Deliberately NOT gated on isAuthenticated. This link is the only thing
          that stamps a Plus signup with the class that caused it (?p=), and most
          people arriving from a creator's marketing are logged out — so gating it
          meant the attribution could only ever fire for people who were already
          signed in. /plans keeps the query string and shows a login modal, so the
          product id survives the sign-in. */}
      {/* Why this isn't free for you. A trial is a subscription in every way the
          page can see — tier 'monthly', status 'trialing' — so without saying it
          out loud, someone on a trial reads "free for members", sees the full
          price, and concludes the site is broken. */}
      {isWebinar && !memberFree && !eventEnded && product.memberPriceCents === 0 && (product.price || 0) > 0 && !access
        && memberStatus?.buyerStatus === 'trialing' && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}>
          <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
            <strong>You're on a free trial, so this class isn't included yet.</strong>{' '}
            {memberStatus?.trialAllowance
              ? `Your trial covers ${memberStatus.trialAllowance} member-priced ${memberStatus.trialAllowance === 1 ? 'event' : 'events'} and you've used ${memberStatus.trialAllowanceUsed ?? 0}.`
              : 'Members attend free. Start your membership and it is free from today.'}
          </p>
        </div>
      )}
      {/* Not on a free event (nothing to save) or an agency-only one (Plus doesn't get you in). */}
      {isWebinar && !memberFree && (!eventEnded || isReplay) && product.memberPriceCents === 0 && (product.price || 0) > 0 && !access && (
        <a
          href={`/plans?c=webinar-joinplus&p=${productId}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
          className="w-full inline-flex items-center justify-center gap-1.5"
          style={{ marginTop: 10, padding: '10px 16px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}
        >
          {isReplay ? 'Or join Hey Joe Plus & watch free →' : 'Or join Hey Joe Plus & attend free →'}
        </a>
      )}
    </>
    )
  );

  return (
    <main className="mx-auto" style={{ maxWidth: 1100, padding: '0 16px 96px', background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      {/* Breadcrumb */}
      {/* Alt landings get a bare logo instead of the marketplace trail — the
          breadcrumb reads "Marketplace / Joe B /" which means nothing to someone
          who arrived from an ad. */}
      {alt ? (
        <div className="flex items-center justify-center" style={{ padding: '18px 0 4px' }}>
          <a href="https://plus.heyjoe.io"><img src="/brand/wordmark-heyjoe.png" alt="Hey Joe" className="dark:[filter:invert(96%)_sepia(8%)_saturate(280%)_hue-rotate(345deg)_brightness(102%)]" style={{ height: 30, width: 'auto', display: 'block' }} /></a>
        </div>
      ) : (
      <div className="flex items-center gap-2.5 flex-wrap" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <Link to="/marketplace" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
          <ChevronLeft size={12} strokeWidth={2} /> Marketplace
        </Link>
        {creator && (
          <>
            <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
            {(creator as any).hidden ? <span>{creator.displayName}</span> : <Link to={`/creators/${creator.slug}`} style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>{creator.displayName}</Link>}
          </>
        )}
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>{displayTitle}</span>
      </div>
      )}

      {/* Mobile-only title — on desktop the title lives in the right panel, which
          on mobile drops below the media/details, so surface it up top here. */}
      <div className="lg:hidden hj-event-paper" style={{ marginTop: 8, marginBottom: 12, padding: '16px 18px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
        <div className="mono-label" style={{ color: 'var(--hj-beige-ink-2)', marginBottom: 6 }}>
          {formatLabel}
        </div>
        <h1 className="font-hj-display m-0" style={{ fontSize: 22, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em', fontWeight: 800, lineHeight: 1.12 }}>
          {displayTitle}
        </h1>
        {datesTBA && (
          <div className="inline-flex items-center gap-1.5" style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>
            <CalendarDays className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink-2)' }} />
            Dates to be announced
          </div>
        )}
        {ev && (
          <div className="inline-flex items-center gap-1.5" style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>
            <CalendarDays className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink-2)' }} />
            {isReplay ? `Recorded ${ev.full}`
              : ((product as any).attendAllDates && moreSessions.length > 1)
                ? (cohortLeft?.started ? `${moreSessions.length} classes left · next ${ev.full}` : `${moreSessions.length} sessions · starts ${ev.full}`)
                : `${ev.full} · ${ev.time}`}
          </div>
        )}
        {lateJoinNote}
        {activeReplayNote}
        <PresenterRail label={hostRailLabel} people={shownHosts} />
      </div>

      {/* One split for every product (Joe, Aug 23 2026). Events used to invert it to
          [1fr_1.35fr], handing the buy box more room than the copy. That reads fine for
          a one-date featured event with a sentence of description, and badly for anything with
          real curriculum to read — which is every class and program we now sell. The
          event treatment (beige paper, date tile) is unchanged; only the widths are. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr]" style={{ marginTop: 8 }}>
        {/* Left: media + curriculum */}
        <div className="flex flex-col gap-5">
          {/* Media / preview */}
          <div className={`relative overflow-hidden flex items-center justify-center ${isEvent && coverSrc && !(showPreview && hasPreview) ? '' : 'aspect-video'}`} style={{ borderRadius: 'var(--hj-r-md)', background: isEvent && !coverSrc ? 'var(--hj-beige)' : 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', ...(isEvent && coverSrc && !(showPreview && hasPreview) ? { maxHeight: 460 } : {}) }}>
            {showPreview && hasPreview ? (
              <>
                {previewMuxId && (!previewIsLesson || previewTokens) ? (
                  <Suspense fallback={<div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'oklch(0.7 0 0)' }} /></div>}>
                    <MuxPlayer
                      key={`mux-preview-${previewMuxId}`}
                      playbackId={previewMuxId}
                      tokens={previewIsLesson && previewTokens ? { playback: previewTokens.playback, thumbnail: previewTokens.thumbnail } : undefined}
                      autoPlay
                      muted={previewMuted}
                      playsInline
                      style={{ width: '100%', height: '100%' }}
                      metadata={{ video_title: `${product.title} preview` }}
                    />
                  </Suspense>
                ) : previewIsLesson && previewTokens === undefined ? (
                  <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'oklch(0.7 0 0)' }} /></div>
                ) : (previewVideo as any)?.url ? (
                  <video src={(previewVideo as any).url} className="w-full h-full" controls autoPlay muted={previewMuted} playsInline />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ color: 'oklch(0.7 0 0)', fontSize: 13 }}>Preview unavailable</div>
                )}
                <button
                  onClick={() => setShowPreview(false)}
                  aria-label="Close preview"
                  className="absolute"
                  style={{ top: 8, right: 8, width: 28, height: 28, borderRadius: 999, background: 'oklch(0 0 0 / 0.6)', border: 0, color: '#fff', cursor: 'pointer', zIndex: 2 }}
                >
                  <X className="w-4 h-4 mx-auto" />
                </button>
              </>
            ) : (
              <>
                {coverSrc ? (
                  // contain + padding so logo-fallback covers don't bleed to the edges.
                  <img src={coverSrc} alt={product.title} className={isEvent ? 'object-contain' : (isLogoCover ? 'w-full h-full object-contain' : 'w-full h-full object-cover')} style={isEvent ? { maxHeight: 440, maxWidth: '100%', width: 'auto', height: 'auto', padding: 12, display: 'block' } : (isLogoCover ? { padding: 44 } : undefined)} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                ) : null}
                {/* Creator brand logo, pinned to the hero's upper corner
                    (admin-set product.brandLogoImage — keeps course branding intact). */}
                {(product as any).brandLogoImage && (
                  <img
                    src={String((product as any).brandLogoImage).startsWith('http') || String((product as any).brandLogoImage).startsWith('/api/') ? (product as any).brandLogoImage : `${STATIC_ROOT}${(product as any).brandLogoImage}`}
                    alt={`${product.title} logo`}
                    className="absolute"
                    style={{ top: 12, left: 12, maxWidth: 120, maxHeight: 56, objectFit: 'contain', zIndex: 2, filter: 'drop-shadow(0 1px 6px oklch(0 0 0 / 0.45))' }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                {!coverSrc && (isEvent && ev ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2" style={{ color: 'var(--hj-beige-ink)' }}>
                    <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', letterSpacing: '0.12em' }}>
                      {creator?.displayName || 'Class'}
                    </div>
                    <div className="flex items-baseline gap-2.5">
                      <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--hj-beige-ink-2)' }}>{ev.month}</span>
                      <span className="font-hj-display" style={{ fontSize: 68, fontWeight: 800, lineHeight: 1, color: 'var(--hj-beige-ink)' }}>{ev.day}</span>
                    </div>
                    {product.instructor && (
                      <div style={{ fontSize: 14, color: 'var(--hj-beige-ink-2)' }}>with {product.instructor}</div>
                    )}
                  </div>
                ) : (
                  <Video className="w-12 h-12 absolute" style={{ color: 'var(--hj-ink-4)' }} />
                ))}
                {hasPreview && (
                  <button
                    className="absolute inset-0 flex items-center justify-center transition-opacity"
                    onClick={() => { setPreviewMuted(false); setShowPreview(true); }}
                    style={{ background: 'oklch(0 0 0 / 0.35)', border: 0, cursor: 'pointer' }}
                  >
                    <div className="inline-flex items-center gap-1.5" style={{ padding: '9px 16px', borderRadius: 999, background: 'var(--hj-bg)', color: 'var(--hj-ink)', fontSize: 13, fontWeight: 600 }}>
                      <Play className="w-4 h-4" /> Watch free preview
                    </div>
                  </button>
                )}
              </>
            )}
          </div>

          {/* Marketing stills (e.g. featured events) — shows what the work looks like.
              Caption gives context: these are real self-tapes, not TV/film stills. */}
          {(product.galleryImages?.length ?? 0) > 0 && (
            <div>
              <div style={{ display: 'inline-block', background: '#0A0A0A', color: 'var(--hj-yellow-ink)', fontSize: 12, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '6px 12px', borderRadius: 6, marginBottom: 10, border: '1px solid var(--hj-hairline)' }}>
                Actual Self-Tape Frames
              </div>
              <div className="grid gap-3" style={{ gridTemplateColumns: (product.galleryImages!.length > 1 ? 'repeat(2, 1fr)' : '1fr') }}>
                {product.galleryImages!.map((img, i) => {
                  const src = img.startsWith('http') || img.startsWith('/api/') ? img : `${STATIC_ROOT}${img}`;
                  return (
                    <img key={i} src={src} alt={`${product.title} — actual self-tape frame ${i + 1}`} className="w-full" style={{ borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)', display: 'block' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  );
                })}
              </div>
            </div>
          )}

          {/* The description belongs in the WIDE column, not the buy panel.
              It used to render inside the aside, so a long one wrapped at ~376px into a
              tall narrow ribbon while the cover image had 626px next to it. That reads
              fine for a one-line blurb (the MYST course) and badly for anything with
              real curriculum in it -- which is what surfaced this (David's Master
              Incubator, 2.7k chars of headings and bullets). The buy panel keeps price,
              tiers and the button; prose lives out here. */}
          {displayDescription && (
            <div className="m-0" style={{ fontSize: 14.5, color: 'var(--hj-ink)', lineHeight: 1.65 }}>
              {renderEmphasis(displayDescription)}
            </div>
          )}

          {/* Past event with a chaptered replay: sell it on what's actually in it. */}
          {isReplay && product.replayChapters?.chapters?.length ? (
            <div>
              <h2 className="font-hj-display m-0" style={{ fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700, marginBottom: 10 }}>
                What's in the replay
              </h2>
              {product.replayChapters.overview && (
                <p className="m-0" style={{ fontSize: 14.5, color: 'var(--hj-ink)', lineHeight: 1.65, marginBottom: 12 }}>{product.replayChapters.overview}</p>
              )}
              <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
                {product.replayChapters.chapters.map((c, i) => (
                  <div key={`${c.startSec}-${i}`} className="flex items-start gap-3" style={{ padding: '11px 16px', borderTop: i ? '1px solid var(--hj-hairline-soft)' : 'none' }}>
                    <span className="mono-label" style={{ minWidth: 54, paddingTop: 2, color: 'var(--hj-ink-2)', fontVariantNumeric: 'tabular-nums' }}>{formatClock(c.startSec)}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>{c.title}</div>
                      {c.summary && <div style={{ fontSize: 13, color: 'var(--hj-ink)', opacity: 0.8, lineHeight: 1.5, marginTop: 2 }}>{c.summary}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Replay questions (Oct 27 2026). Everyone sees the active-replay promise, the
              question count and the host's answers; people who have the replay get the
              ask box here too, so the emails that land on this page lead somewhere. */}
          {isReplay && product._id && (
            <ReplayQuestions productId={String(product._id)} publicFallback />
          )}

          {/* Bundle / season pass: the listings it unlocks, each linking to its own page. */}
          {isBundle ? (
            <div>
              <div className="flex items-baseline gap-3" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                <h2 className="font-hj-display m-0" style={{ fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
                  {isPass ? 'Events on this pass' : 'What’s in the bundle'}
                </h2>
                <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{bundle?.items.length || 0} item{(bundle?.items.length || 0) !== 1 ? 's' : ''}</span>
              </div>
              {isPass && bundle?.through && (
                <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink)', lineHeight: 1.55, marginBottom: 12 }}>
                  Covers every featured event {creator?.displayName || 'this host'} runs through {new Date(bundle.through).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles' })}, including ones added after you buy.
                </p>
              )}
              <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
                {(bundle?.items || []).length === 0 && (
                  <div style={{ padding: '13px 16px', fontSize: 14, color: 'var(--hj-ink)' }}>
                    {isPass ? 'Nothing is scheduled yet. Events are added here as they are announced.' : 'Nothing is listed in this bundle yet.'}
                  </div>
                )}
                {(bundle?.items || []).map((it, i) => {
                  const img = it.coverImage ? (it.coverImage.startsWith('http') || it.coverImage.startsWith('/api/') ? it.coverImage : `${STATIC_ROOT}${it.coverImage}`) : null;
                  const when = it.sessions.length > 1
                    ? `${it.sessions.length} sessions from ${new Date(it.sessions[0]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                    : (it.sessions[0] || it.eventDate) ? new Date(it.sessions[0] || it.eventDate!).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
                    : it.productType === 'series' ? 'Course' : it.format === 'series' ? 'Series' : 'Video';
                  return (
                    <Link key={it._id} to={`/course/${it._id}`} className="flex items-center gap-3"
                      style={{ padding: '12px 14px', borderTop: i ? '1px solid var(--hj-hairline-soft)' : 'none', color: 'var(--hj-ink)', textDecoration: 'none', minHeight: 56 }}>
                      {img ? <img src={img} alt="" style={{ width: 64, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                        : <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 64, height: 40, borderRadius: 6, background: 'var(--hj-surface-2)' }}><Video className="w-4 h-4" /></span>}
                      <span className="flex-1 min-w-0">
                        <span className="block" style={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.3 }}>{it.title}</span>
                        <span className="block" style={{ fontSize: 12.5, color: 'var(--hj-ink)', opacity: 0.8, marginTop: 2 }}>
                          {when}{it.tierLabel ? ` · ${it.tierLabel}` : ''}
                        </span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, flexShrink: 0, textDecoration: it.priceCents ? 'line-through' : 'none', opacity: 0.75 }}>
                        {it.priceCents ? formatPrice(it.priceCents * Math.max(1, it.sessions.length)) : 'Free'}
                      </span>
                    </Link>
                  );
                })}
              </div>
              {bundle && bundle.saveCents > 0 && (
                <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink)', marginTop: 10 }}>
                  Bought one by one: {formatPrice(bundle.separateCents)}. With the {isPass ? 'pass' : 'bundle'}: <strong>{formatPrice(product.price)}</strong>. You save {formatPrice(bundle.saveCents)}.
                </p>
              )}
            </div>
          ) :
          /* Class details (dated events) or curriculum (courses/products) */
          isEvent && ev ? (
            <div>
              <div className="flex items-baseline gap-3" style={{ marginBottom: 12 }}>
                <h2 className="font-hj-display m-0" style={{ fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
                  {formatLabel} details
                </h2>
              </div>
              <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
                <div className="flex items-center gap-3" style={{ padding: '13px 16px' }}>
                  <CalendarDays className="w-4 h-4" style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }} />
                  <span style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>{isReplay ? `Recorded ${ev.full}` : ev.full}</span>
                </div>
                <div className="flex items-center gap-3" style={{ padding: '13px 16px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
                  <Clock className="w-4 h-4" style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }} />
                  <span style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>
                    {/* Past event: the start time is meaningless, runtime isn't. */}
                    {isReplay
                      ? (typeof product.durationMins === 'number' && product.durationMins > 0 ? formatDuration(product.durationMins) : 'Watch anytime')
                      : `${ev.time}${typeof product.durationMins === 'number' && product.durationMins > 0 ? ` · ${formatDuration(product.durationMins)}` : ''}`}
                  </span>
                </div>
                {classFormat && (
                  <div className="flex items-center gap-3" style={{ padding: '13px 16px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
                    <MapPin className="w-4 h-4" style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13.5, color: 'var(--hj-ink)', fontWeight: 600 }}>{classFormat}</span>
                  </div>
                )}
                {hasVenue && (
                  <div className="flex items-start gap-3" style={{ padding: '13px 16px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
                    <MapPin className="w-4 h-4" style={{ color: 'var(--hj-ink-3)', flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>
                      <span style={{ fontWeight: 600 }}>{(product as any).locationLabel || (hasOnlineRoom ? 'In person + online' : 'In person')}</span>
                      {venueLineOf(headlineVenue) && <><br />{venueLineOf(headlineVenue)}</>}
                      {headlineVenue?.notes && <><br /><span style={{ color: 'var(--hj-ink-2)', fontSize: 12.5 }}>{headlineVenue.notes}</span></>}
                      {!headlineVenue?.address && (
                        <><br /><span style={{ color: 'var(--hj-ink-2)', fontSize: 12.5 }}>Exact address goes out when you register.</span></>
                      )}
                    </span>
                  </div>
                )}
                {instructorOffice && (
                  <div className="flex items-center gap-3" style={{ padding: '13px 16px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
                    <Building2 className="w-4 h-4" style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>{instructorOffice}</span>
                  </div>
                )}
                {typeof product.seatsRemaining === 'number' && product.seatsRemaining > 0 && (
                  <div className="flex items-center gap-3" style={{ padding: '13px 16px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
                    <Ticket className="w-4 h-4" style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>Seats remaining</span>
                  </div>
                )}
                {cancellationHours !== null && (
                  <div className="flex items-center gap-3" style={{ padding: '13px 16px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
                    <RotateCcw className="w-4 h-4" style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13.5, color: 'var(--hj-ink)' }}>Cancel up to {cancellationHours} hours before</span>
                  </div>
                )}
              </div>
              {isRecurringWebinar && !eventEnded ? (
                <div style={{ marginTop: 14 }}>
                  <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 8 }}>Choose your session</div>
                  <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
                    {sessionOptions.map((s, i) => {
                      const booked = sessionFull(s.key);
                      const active = !booked && chosenSession === s.key;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          disabled={booked}
                          onClick={() => setSelectedSession(s.key)}
                          className="flex items-center gap-3 w-full text-left transition-colors"
                          style={{
                            padding: '12px 16px',
                            borderTop: i === 0 ? 'none' : '1px solid var(--hj-hairline-soft)',
                            background: active ? 'var(--hj-blue)' : 'transparent',
                            border: 0,
                            cursor: booked ? 'not-allowed' : 'pointer',
                            textDecoration: booked ? 'line-through' : 'none',
                            fontFamily: 'inherit',
                          }}
                        >
                          <span
                            aria-hidden
                            style={{
                              width: 16, height: 16, borderRadius: 999, flexShrink: 0,
                              border: `2px solid ${active ? 'oklch(0.18 0.005 70)' : 'var(--hj-hairline)'}`,
                              background: active ? 'oklch(0.18 0.005 70)' : 'transparent',
                              boxShadow: active ? 'inset 0 0 0 3px var(--hj-blue)' : 'none',
                            }}
                          />
                          <span style={{ fontSize: 13.5, fontWeight: active ? 700 : 500, color: active ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)' }}>
                            {s.full} · {s.time}
                            {/* Who is coming to THAT date, so the picker says something
                                about each option instead of just its timestamp. */}
                            {(() => {
                              const g = occHostsFor(s.key);
                              const n = enrolledFor(s.key);
                              // A time slot says Booked or Open, never a head count.
                              const tally = sessionSeatCap > 0 ? (booked ? 'Booked' : 'Open') : (n ? `${n} enrolled` : null);
                              if (!g.length && !tally) return null;
                              return (
                                <><br /><span style={{ fontSize: 12, fontWeight: 500, color: active ? 'oklch(0.28 0.005 70)' : 'var(--hj-ink-3)' }}>
                                  {[g.map(h => h.name).join(' & ') || null, tally].filter(Boolean).join(' · ')}
                                </span></>
                              );
                            })()}
                          </span>
                          {/* The faces belong on the row you pick, not only on the one
                              already selected — that is what makes a week worth choosing. */}
                          {(() => {
                            const faces = occHostsFor(s.key).filter(h => h.image).slice(0, 3);
                            if (!faces.length) return null;
                            return (
                              <span className="flex flex-shrink-0" style={{ marginLeft: 'auto', paddingLeft: 8 }}>
                                {faces.map((h, fi) => (
                                  <img
                                    key={`${h.name}-${fi}`}
                                    src={h.image!.startsWith('http') || h.image!.startsWith('/api/') ? h.image! : `${STATIC_ROOT}${h.image}`}
                                    alt={h.name}
                                    className="hj-headshot"
                                    style={{ width: 32, height: 32, marginLeft: fi ? -8 : 0, boxShadow: '0 0 0 2px var(--hj-surface-1)' }}
                                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                ))}
                              </span>
                            );
                          })()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : moreSessions.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 8 }}>{(product as any).attendAllDates ? 'Full schedule — included' : 'More sessions'}</div>
                  <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
                    {moreSessions.slice(0, 5).map((s, i) => {
                      // A club that meets somewhere different every week: each date
                      // carries its own venue and its own write-up.
                      const occ = occurrenceFor(s.key);
                      const v = venueFor(s.key);
                      const line = occ && venueLineOf(v);
                      return (
                        <div key={s.key} className="flex items-start gap-3" style={{ padding: '11px 16px', borderTop: i === 0 ? 'none' : '1px solid var(--hj-hairline-soft)' }}>
                          <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--hj-ink-3)', marginTop: 3 }} />
                          <span style={{ fontSize: 13, color: 'var(--hj-ink)' }}>
                            {s.full} · {s.time}
                            {line && <><br /><span style={{ color: 'var(--hj-ink-2)', fontSize: 12.5 }}>{line}</span></>}
                            {occ?.details && <><br /><span style={{ color: 'var(--hj-ink-2)', fontSize: 12.5, whiteSpace: 'pre-line' }}>{occ.details}</span></>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {moreSessions.length > 5 && (
                    <p className="m-0" style={{ marginTop: 6, fontSize: 12, color: 'var(--hj-ink-2)' }}>+{moreSessions.length - 5} more</p>
                  )}
                </div>
              )}
              {headlineDetails && (
                <div style={{ marginTop: 14 }}>
                  <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 8 }}>What&rsquo;s on this time</div>
                  <p className="m-0" style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--hj-ink)', whiteSpace: 'pre-line' }}>{headlineDetails}</p>
                </div>
              )}
              <div style={{ marginTop: 14 }}>{registerCta('details')}{offerNudge()}</div>
            </div>
          ) : (
          <div>
            <div className="flex items-baseline gap-3" style={{ marginBottom: 12 }}>
              <h2 className="font-hj-display m-0" style={{ fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
                {isSeries ? 'Course content' : isVideoSeries ? 'Episodes' : 'What’s included'}
              </h2>
              <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
            </div>

            <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
              {isSeries ? (
                // Chapter numbers run 1–N across the whole course, not
                // restarting per module (David's MYST feedback).
                (() => {
                  let flat = 0;
                  return product.modules!.map((m, mi) => {
                    const start = flat;
                    flat += (m.lessons || []).length;
                    return (
                      <div key={m.id || mi} style={{ borderTop: mi === 0 ? 'none' : '1px solid var(--hj-hairline)' }}>
                        <div style={{ padding: '11px 14px', background: 'var(--hj-surface-2)' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--hj-ink)' }}>{m.title || `Module ${mi + 1}`}</span>
                          <span className="mono-label" style={{ marginLeft: 8, color: 'var(--hj-ink-3)' }}>{(m.lessons || []).length}</span>
                        </div>
                        {(m.lessons || []).map((l, li) => renderItemRow(l, start + li))}
                      </div>
                    );
                  });
                })()
              ) : (
                product.videos.map((v, i) => renderItemRow(v, i))
              )}
            </div>
          </div>
          )}

          {/* Testimonials — social proof (e.g. casting directors on a featured event) */}
          {(product.testimonials?.length ?? 0) > 0 && (
            <div>
              <div className="flex items-baseline gap-3" style={{ marginBottom: 12 }}>
                <h2 className="font-hj-display m-0" style={{ fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
                  {testimonialHeading}
                </h2>
              </div>
              <div className="flex flex-col gap-3">
                {product.testimonials!.map((t, i) => (
                  <div key={i} style={{ borderLeft: '3px solid var(--hj-yellow)', padding: '4px 0 4px 16px' }}>
                    <p className="m-0" style={{ fontStyle: 'italic', fontSize: 15, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
                      “{t.quote}”
                    </p>
                    <p className="m-0" style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink-3)', fontWeight: 600 }}>
                      {t.name}{t.title ? ` · ${t.title}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: purchase panel */}
        <aside className="flex flex-col gap-4">
          {previewOnly && (
            <div style={{ padding: '12px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow)', color: '#0A0A0A', fontSize: 13, lineHeight: 1.45 }}>
              <strong>Preview.</strong> This isn&rsquo;t published yet, so only people with this link can see it. Registration opens when it goes live.
            </div>
          )}
          {/* Edit: owners and admins. viewerIsOwner comes from the server via
              viewerOwnsProduct, which honors ADMIN_EMAILS — so this is the way in
              for a product that never appears in your own My Store list. */}
          {viewerIsOwner && (
            <button
              type="button"
              onClick={() => navigate(
                product.productType === 'series' ? `/my-store/course/${productId}`
                : isBundle ? `/my-store/bundle/${productId}`
                : product.productType === 'webinar' ? `/my-store/webinar/${productId}`
                : `/my-store/video/${productId}`
              )}
              className="w-full inline-flex items-center justify-center gap-2"
              style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', padding: '11px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
            >
              <Pencil className="w-4 h-4" /> Edit this {product.productType === 'webinar' ? 'webinar' : product.productType === 'series' ? 'course' : isBundle ? (isPass ? 'pass' : 'bundle') : 'listing'}
            </button>
          )}
          {/* Creator view: the owner (or an admin) watches everything without buying —
              the full course experience lives on the purchases viewer, which the
              /access endpoint already unlocks for creators. */}
          {viewerIsOwner && !isWebinar && !isBundle && (
            <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-blue)', padding: 16 }}>
              <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 6 }}>Creator view</div>
              <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
                This is your {isSeries ? 'course' : 'listing'} — watch every chapter and video without purchasing. Buyers see the locks below.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/purchases/${productId}`)}
                className="w-full inline-flex items-center justify-center gap-2"
                style={{ ...primaryPill, marginTop: 12 }}
              >
                <Play className="w-4 h-4" /> Watch the full {isSeries ? 'course' : 'video'}
              </button>
            </div>
          )}
          <div className={isEvent ? 'hj-event-paper' : ''} style={{ borderRadius: 'var(--hj-r-md)', background: isEvent ? 'var(--hj-beige)' : 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', padding: 20 }}>
            {/* Title/label/date — hidden on mobile (shown up top instead), visible on desktop */}
            <div className="hidden lg:block">
              <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 8 }}>
                {formatLabel}
              </div>
              <h1 className="font-hj-display m-0" style={{ fontSize: 24, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 800, lineHeight: 1.1 }}>
                {displayTitle}
              </h1>
              {datesTBA && (
                <div className="inline-flex items-center gap-1.5" style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: 'var(--hj-ink-2)' }}>
                  <CalendarDays className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />
                  Dates to be announced
                </div>
              )}
              {ev && (
                <div className="inline-flex items-center gap-1.5" style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: 'var(--hj-ink-2)' }}>
                  <CalendarDays className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-3)' }} />
                  {/* A cohort is not "on" one date. Leading with only the first session
                      made a two-month program look like a single afternoon. */}
                  {isReplay ? `Recorded ${ev.full}`
                    : ((product as any).attendAllDates && moreSessions.length > 1)
                      ? (cohortLeft?.started ? `${moreSessions.length} classes left · next ${ev.full}` : `${moreSessions.length} sessions · starts ${ev.full}`)
                      : `${ev.full} · ${ev.time}`}
                </div>
              )}
              {lateJoinNote}
              {activeReplayNote}
              <PresenterRail label={hostRailLabel} people={shownHosts} />
            </div>

            <div className="flex items-center gap-3" style={{ marginTop: 10 }}>
              {reviews && reviews.totalReviews > 0 && (
                <span className="inline-flex items-center gap-1 tabular" style={{ fontSize: 12.5, color: 'var(--hj-ink-3)' }}>
                  {/* Event panel is cream paper — brand yellow stars vanish on it, so use paper ink there. */}
                  <span style={{ color: isEvent ? 'var(--hj-beige-ink)' : 'var(--hj-yellow)' }}>
                    {'★'.repeat(Math.round(reviews.averageRating))}{'☆'.repeat(5 - Math.round(reviews.averageRating))}
                  </span>
                  <span>({reviews.totalReviews})</span>
                </span>
              )}
              {shownEnrolled > 0 && (
                <span className="inline-flex items-center gap-1" style={{ fontSize: 12.5, color: 'var(--hj-ink-3)' }}>
                  <Users className="w-3.5 h-3.5" /> {shownEnrolled} enrolled{isRecurringWebinar ? ' this date' : ''}
                </span>
              )}
            </div>


            {/* What every seat carries regardless of how they paid. Lives beside the
                price rather than only in the description, because this is the part that
                justifies the number they are looking at. */}
            {Array.isArray((product as any).includedItems) && (product as any).includedItems.length > 0 && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--hj-hairline)' }}>
                <div className="mono-label" style={{ color: isEvent ? 'var(--hj-beige-ink)' : 'var(--hj-ink-3)', marginBottom: 8 }}>Every seat includes</div>
                {((product as any).includedItems as string[]).map((it, i) => (
                  <div key={i} className="flex items-start gap-2" style={{ marginBottom: 6, fontSize: 13, lineHeight: 1.45, color: 'var(--hj-ink)' }}>
                    <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ marginTop: 3, color: isEvent ? 'var(--hj-beige-ink)' : 'var(--hj-green)' }} />
                    <span>{it}</span>
                  </div>
                ))}
              </div>
            )}

            {tiers ? (
              /* Ticket tier picker — participant vs observer seats. Replaces the
                 single price; the buy button charges the selected tier. */
              <div style={{ marginTop: 16 }} className="flex flex-col gap-2" role="radiogroup" aria-label="Ticket type">
                {tiers.map((t) => {
                  const sel = (activeTier?.key || null) === t.key;
                  // Members can't buy a non-members-only tier; show it, greyed, with the reason.
                  const forNonMembersOnly = !!(t as any).nonMembersOnly && !!memberStatus?.isPlusMember;
                  // A members-only tier greys for a signed-in non-member; signed out it stays
                  // pickable and the button reads "Sign in", which is the right door.
                  const forMembersOnly = !!(t as any).membersOnly && isAuthenticated && !memberStatus?.isPlusMember;
                  const out = !!t.soldOut || forNonMembersOnly || forMembersOnly;
                  // The signed-in buyer's resolved deal for this tier, if any.
                  const d = tierDeal(t.key);
                  const hasDeal = !!(d && d.memberRate && d.chargeCents < t.priceCents);
                  const rowCharge = hasDeal ? d!.chargeCents : t.priceCents;
                  // Pre-auth preview: what a member WOULD pay (annual free / monthly price).
                  const annualPreview = typeof t.annualMemberPriceCents === 'number' && t.annualMemberPriceCents < t.priceCents;
                  const monthlyPreview = typeof t.memberPriceCents === 'number' && t.memberPriceCents < t.priceCents;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      role="radio"
                      aria-checked={sel}
                      disabled={out}
                      onClick={() => setSelectedTier(t.key)}
                      className="w-full text-left"
                      style={{
                        padding: '12px 14px',
                        borderRadius: 'var(--hj-r-sm)',
                        cursor: out ? 'not-allowed' : 'pointer',
                        background: sel ? 'var(--hj-surface-2)' : 'transparent',
                        // NOT --hj-blue: aliased to the brand yellow, which as a 2px
                        // rule on cream is barely a selection at all. --hj-ink tracks
                        // whichever surface this panel is rendering on.
                        border: sel ? '2px solid var(--hj-ink)' : '1px solid var(--hj-hairline)',
                        // Keep the 2px selected border from shifting layout.
                        margin: sel ? 0 : 1,
                        opacity: out ? 0.55 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-ink)' }}>{t.label}</span>
                        <span className="inline-flex items-baseline gap-2">
                          {hasDeal && (
                            <span className="tabular" style={{ fontSize: 12.5, color: 'var(--hj-ink-4)', textDecoration: 'line-through', fontFamily: 'var(--hj-font-mono)' }}>
                              {formatPrice(t.priceCents)}
                            </span>
                          )}
                          <span className="tabular" style={{ fontSize: 15, fontWeight: 800, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                            {rowCharge === 0 ? 'Free' : formatPrice(rowCharge)}
                          </span>
                        </span>
                      </span>
                      {t.description && (
                        <span className="block" style={{ marginTop: 3, fontSize: 12.5, color: 'var(--hj-ink)', lineHeight: 1.45 }}>{renderRichText(t.description, { inlineOnly: true })}</span>
                      )}
                      {hasDeal ? (
                        /* Signed-in member: their actual deal, plainly named. */
                        <span className="inline-flex items-center gap-1.5" style={{ marginTop: 4 }}>
                          <span className="mono-label inline-flex" style={{ padding: '1px 6px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 9.5, fontWeight: 700 }}>HJ+</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--hj-ink)' }}>
                            {d!.annualFreeClass ? 'Your free class this year' : 'Member price'}
                          </span>
                        </span>
                      ) : (annualPreview || monthlyPreview) && (
                        /* Pre-auth / non-member preview of what Plus members pay. */
                        <span className="inline-flex items-center gap-1.5" style={{ marginTop: 4 }}>
                          <span className="mono-label inline-flex" style={{ padding: '1px 6px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 9.5, fontWeight: 700 }}>HJ+</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--hj-ink)' }}>
                            {annualPreview && (t.annualMemberPriceCents === 0 ? 'Free for annual members' : `${formatPrice(t.annualMemberPriceCents!)} for annual members`)}
                            {annualPreview && monthlyPreview && ' · '}
                            {monthlyPreview && `${t.memberPriceCents === 0 ? 'Free' : formatPrice(t.memberPriceCents!)} for members`}
                          </span>
                        </span>
                      )}
                      {t.plusOfferMonths ? (
                        /* Plus months bundled with THIS tier (e.g. the $75 working seat). */
                        <span className="inline-flex items-center gap-1.5" style={{ marginTop: 4 }}>
                          <span className="mono-label inline-flex" style={{ padding: '1px 6px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 9.5, fontWeight: 700 }}>HJ+</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--hj-ink)' }}>
                            + {t.plusOfferMonths} month{t.plusOfferMonths === 1 ? '' : 's'} of Hey Joe Plus, free
                          </span>
                        </span>
                      ) : null}
                      {out ? (
                        <span className="block" style={{ marginTop: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--hj-ink)' }}>{forNonMembersOnly ? 'For non-members (you already have Plus)' : forMembersOnly ? 'For Hey Joe Plus members' : t.priceCents === 0 ? 'Full' : 'Sold out'}</span>
                      ) : typeof t.seatsRemaining === 'number' ? (
                        /* Capped tier: show live availability from the start and let it
                           count down as people book (backend recomputes on each load). */
                        <span className="block" style={{ marginTop: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--hj-deep-blue)' }}>
                          {t.seatsRemaining} spot{t.seatsRemaining === 1 ? '' : 's'} left
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : (
            <div style={{ marginTop: 16 }}>
              {memberStatus?.studioMemberRate ? (
                <>
                  <div className="inline-flex items-baseline gap-2">
                    <span className="tabular" style={{ fontSize: 30, fontWeight: 800, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                      {memberStatus.chargeCents === 0 ? 'Free' : formatPrice(memberStatus.chargeCents)}
                    </span>
                    <span className="tabular" style={{ fontSize: 16, color: 'var(--hj-ink-4)', textDecoration: 'line-through', fontFamily: 'var(--hj-font-mono)' }}>
                      {formatPrice(memberStatus.regularPriceCents)}
                    </span>
                  </div>
                  <span className="mono-label inline-flex" style={{ marginTop: 6, padding: '2px 8px', borderRadius: 999, background: 'var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 10, fontWeight: 700 }}>
                    {creator?.displayName || 'Studio'} member price
                  </span>
                </>
              ) : memberStatus?.memberRate && memberStatus.memberPriceCents != null ? (
                <>
                  <div className="inline-flex items-baseline gap-2">
                    <span className="tabular" style={{ fontSize: 30, fontWeight: 800, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                      {formatPrice(memberStatus.memberPriceCents)}
                    </span>
                    <span className="tabular" style={{ fontSize: 16, color: 'var(--hj-ink-4)', textDecoration: 'line-through', fontFamily: 'var(--hj-font-mono)' }}>
                      {formatPrice(memberStatus.regularPriceCents)}
                    </span>
                  </div>
                  <span className="mono-label inline-flex" style={{ marginTop: 6, padding: '2px 8px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 10, fontWeight: 700 }}>
                    HJ+ member price
                  </span>
                  {memberStatus.annualLimit != null && memberStatus.used != null && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--hj-ink-3)' }}>
                      {memberStatus.used} of {memberStatus.annualLimit} member-rate classes used this year
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="tabular" style={{ fontSize: 30, fontWeight: 800, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                    {product.pricingMode === 'subscription' && product.price === 0 ? 'Free' : formatPrice(product.price)}
                    {lateJoin && (
                      <span style={{ marginLeft: 10, fontSize: 18, fontWeight: 600, color: 'var(--hj-ink-2)', textDecoration: 'line-through' }}>{formatPrice(lateJoin.fullPrice)}</span>
                    )}
                    {product.pricingMode === 'subscription' && product.price > 0 && (
                      <span style={{ fontSize: 14, color: 'var(--hj-ink-3)', fontWeight: 400 }}>/mo</span>
                    )}
                  </div>
                  {/* HJ+ member price shown to everyone as an upsell — exact eligibility +
                      annual cap resolve at checkout (or via memberStatus when signed in). */}
                  {typeof product.memberPriceCents === 'number' && product.memberPriceCents < product.price && (
                    <div className="inline-flex items-center gap-2" style={{ marginTop: 8 }}>
                      <span className="mono-label inline-flex" style={{ padding: '2px 8px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 10, fontWeight: 700 }}>
                        HJ+
                      </span>
                      <span className="tabular" style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                        {formatPrice(product.memberPriceCents)}
                      </span>
                      <span style={{ fontSize: 12.5, color: 'var(--hj-ink-3)' }}>member price</span>
                    </div>
                  )}
                  {/* Studio membership price (founding Studio partners), shown to everyone */}
                  {typeof product.studioMemberPriceCents === 'number' && product.studioMemberPriceCents < product.price && (
                    <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 8 }}>
                      <span className="tabular" style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                        {product.studioMemberPriceCents === 0 ? 'Free' : formatPrice(product.studioMemberPriceCents)}
                      </span>
                      <span style={{ fontSize: 12.5, color: 'var(--hj-ink-3)' }}>for {creator?.displayName || 'studio'} members</span>
                    </div>
                  )}
                </>
              )}
              {/* A class that runs both ways at one price says so here, where a
                  ticket-type picker would otherwise have shown the choice. */}
              {(product as any).locationLabel && (
                <div style={{ marginTop: 10, fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)' }}>{(product as any).locationLabel}</div>
              )}
            </div>
            )}

            {/* Over the member-rate cap → booking closed here; the studio wants them on its own site. */}
            {memberStatus?.overCap && (() => {
              const site = memberStatus.upsellUrl ? memberStatus.upsellUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') : null;
              const studio = creator?.displayName || 'this studio';
              return (
                <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)' }}>
                  <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
                    You've already used your {memberStatus.annualLimit === 1 ? 'one' : memberStatus.annualLimit} {studio} member-rate {memberStatus.annualLimit === 1 ? 'class' : 'classes'}.
                    {site ? <> Please visit <a href={memberStatus.upsellUrl!} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hj-ink)', fontWeight: 700 }}>{site}</a> to book your next class and join as a member.</> : null}
                  </p>
                </div>
              );
            })()}

            {/* Discount code — hidden behind a quiet link; agency launch codes etc. */}
            {product.pricingMode !== 'subscription' && !memberFree && (
              <div style={{ marginTop: 12 }}>
                {appliedDiscount ? (
                  <div className="flex items-center justify-between gap-2" style={{ padding: '8px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--hj-ink)' }}>
                      {appliedDiscount.code} — {formatPrice(appliedDiscount.amountOffCents)} off applied
                      {/* A promo deadline is only worth claiming in an ad if the page
                          states it and checkout enforces it — both are true here. */}
                      {appliedDiscount.expiresAt && (
                        <span style={{ fontWeight: 500 }}>
                          {' · offer ends '}
                          {new Date(appliedDiscount.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </span>
                    <button type="button" onClick={() => setAppliedDiscount(null)} aria-label="Remove code" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hj-ink-2)', padding: 2 }}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : showCodeField ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={codeInput}
                      onChange={e => setCodeInput(e.target.value.toUpperCase())}
                      onKeyDown={e => { if (e.key === 'Enter') handleApplyCode(); }}
                      placeholder="Discount code"
                      className="flex-1 min-w-0"
                      style={{ padding: '9px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 13.5, fontFamily: 'var(--hj-font-mono)', textTransform: 'uppercase', outline: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={handleApplyCode}
                      disabled={applyingCode || !codeInput.trim()}
                      style={{ padding: '9px 16px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      {applyingCode ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply'}
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setShowCodeField(true)} style={{ background: 'none', border: 'none', padding: 0, fontSize: 12.5, color: 'var(--hj-ink-2)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>
                    Have a discount code?
                  </button>
                )}
              </div>
            )}

            {/* Hey Joe credit — referral rewards, applied to the ticket. Same rounding
                rule as the server: a remainder under 50¢ leaves 50¢ on the card. */}
            {isAuthenticated && walletCents > 0 && product.pricingMode !== 'subscription' && !memberFree && (() => {
              const cartCents = activeTier ? withCode(activeTierCharge!) : withCode(memberStatus?.chargeCents ?? product.price);
              if (cartCents <= 0) return null;
              let apply = Math.min(walletCents, cartCents);
              const rem = cartCents - apply;
              if (rem > 0 && rem < 50) apply = Math.max(0, cartCents - 50);
              const youPay = cartCents - apply;
              return (
                <label className="flex items-start gap-3" style={{ marginTop: 12, padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)', cursor: 'pointer' }}>
                  <input type="checkbox" id="use-wallet-credit" checked={useWallet} onChange={e => setUseWallet(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, accentColor: 'var(--hj-ink)' }} />
                  <span style={{ fontSize: 13, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700 }}>Use {formatPrice(apply)} of your Hey Joe credit</span>
                    <span style={{ display: 'block', color: 'var(--hj-ink-2)', fontSize: 12.5 }}>
                      {useWallet
                        ? (youPay === 0 ? 'Covers the whole ticket. Nothing to pay today.' : `You pay ${formatPrice(youPay)} today.`)
                        : `You have ${formatPrice(walletCents)} available.`}
                    </span>
                  </span>
                </label>
              );
            })()}

            <div style={{ marginTop: 16 }}>{registerCta('side')}{offerNudge()}</div>

            <button
              type="button"
              onClick={handleShare}
              className="w-full inline-flex items-center justify-center gap-1.5"
              style={{ marginTop: 10, padding: '10px 16px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
            >
              <Share2 className="w-3.5 h-3.5" /> Share this {isBundle ? (isPass ? 'pass' : 'bundle') : 'course'}
            </button>

            {!isAuthenticated && (
              <p style={{ marginTop: 12, fontSize: 12, color: 'var(--hj-ink-2)', textAlign: 'center' }}>
                You can browse and preview without an account. Sign in to purchase.
              </p>
            )}
          </div>

          {/* Creator card */}
          {creator && !(creator as any).hidden && (
            <Link
              to={`/creators/${creator.slug}`}
              className="flex items-center gap-3"
              style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', padding: 14, textDecoration: 'none' }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt={creator.displayName} className="hj-headshot object-cover flex-shrink-0" style={{ width: 44, height: 44 }} />
              ) : (
                <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, background: 'var(--hj-yellow-tint)', color: 'var(--hj-yellow-ink)', fontWeight: 700, fontFamily: 'var(--hj-font-mono)' }}>
                  {creator.displayName?.[0]}
                </div>
              )}
              <div className="min-w-0">
                <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Created by</div>
                <div className="truncate" style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>{creator.displayName}</div>
              </div>
            </Link>
          )}
        </aside>
      </div>

      {/* Reviews */}
      {reviews && reviews.reviews.length > 0 && (
        <section style={{ marginTop: 36 }}>
          <h2 className="font-hj-display m-0" style={{ fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700, marginBottom: 14 }}>
            Reviews
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {reviews.reviews.slice(0, 6).map(r => (
              <div key={r._id} style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', padding: 14 }}>
                <div className="flex items-center justify-between">
                  <span style={{ color: 'var(--hj-yellow-ink)', fontSize: 13 }}>
                    {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--hj-ink-4)' }}>{r.buyerEmail}</span>
                </div>
                {r.comment && <p className="m-0" style={{ marginTop: 8, fontSize: 13, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>{r.comment}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="text-center" style={{ padding: '40px 0 12px' }}>
        <p className="mono-label" style={{ color: 'var(--hj-ink-4)' }}>
          Powered by{' '}
          <a href="https://post.heyjoe.io" style={{ color: 'var(--hj-ink-3)', textDecoration: 'underline' }}>Hey Joe Plus</a>
        </p>
      </div>
    </main>
  );
};

export default CoursePage;
